import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { assetUrl, fetchJSON } from '../../utils/api';
import {
  getDebotConfigs,
  getDefaulterTerminalFaqs,
  getFrontpageThreeDAndTerminalEnabled,
  subscribeDefaulterTerminalFaqs,
  subscribeDebotConfigs,
  subscribeFrontpageThreeDAndTerminalEnabled,
} from '../../utils/debots';
import AnimatedTerminalShell from '../common/AnimatedTerminalShell';
import PhotoGallery3D from './PhotoGallery3D';
import GalleryCategoryOverview from './GalleryCategoryOverview';
import './ExploreGallery.css';

const MAX_TERMINAL_LINES = 280;
const THREE_D_TRANSITION_CODE_MS = 2000;
const THREE_D_TRANSITION_WHITE_BLUR_MS = 1000;
const THREE_D_TRANSITION_FADE_OUT_MS = 1000;
const THREE_D_TRANSITION_ARRIVAL_WHITE_MS = 1000;
const THREE_D_TRANSITION_ARRIVAL_CLEAR_MS = 1000;
const THREE_D_TRANSITION_UI_MS = 1000;
const THREE_D_SCENE_OPACITY_TRANSITION_MS = 2520;
const THREE_D_SCENE_FILTER_TRANSITION_MS = 840;
const THREE_D_ITEM_OPACITY_TRANSITION_MS = 2000;
const TRANSITION_TO_3D_MS = THREE_D_TRANSITION_CODE_MS
  + THREE_D_TRANSITION_WHITE_BLUR_MS
  + THREE_D_TRANSITION_FADE_OUT_MS
  + THREE_D_TRANSITION_ARRIVAL_WHITE_MS
  + THREE_D_TRANSITION_ARRIVAL_CLEAR_MS;
const TRANSITION_TO_2D_MS = 1200;
const SMOKE_TRANSITION_MS = 980;
const THREE_D_EXPLORE_BUTTON_HEIGHT_SCALE = 0.7;
const TWO_D_TERMINAL_AUTO_OPEN_DELAY_MS = 1000;
const TWO_D_TERMINAL_BUTTON_LEFT = 28;
const TWO_D_TERMINAL_BUTTON_BOTTOM = 22;
const TWO_D_TERMINAL_BUTTON_HEIGHT = 62;
const TWO_D_TERMINAL_PANEL_LEFT = 28;
const TWO_D_TERMINAL_PANEL_WIDTH = 560;
const TWO_D_TERMINAL_PANEL_HEIGHT = 500;
const TWO_D_TERMINAL_EXPAND_SCALE = 2;
const TWO_D_TERMINAL_EXPAND_SETTLE_MS = 360;
const TERMINAL_TYPEWRITER_CHARS_PER_SEC = 36;
const TWO_D_MEDIA_TRANSITION_MS = 500;
const TWO_D_GALLERY_STAGE_MS = 760;
const TWO_D_FOCUSED_SWITCH_COLLAPSE_MS = 500;
const TWO_D_FOCUSED_SWITCH_WHITE_PULSE_MS = 1000;
const TWO_D_FOCUSED_SWITCH_TITLE_PULSE_MS = 1000;
const TWO_D_FOCUSED_SWITCH_RESTORE_MS = 500;
const TWO_D_PRESS_ZOOM_TRANSITION_MS = 700;
const TWO_D_HOLD_TO_ZOOM_DELAY_MS = 110;
const TWO_D_HOLD_ZOOM_BASE_SCALE = 2.8;
const TWO_D_HOLD_ZOOM_OFFSET_MIN = -1.8;
const TWO_D_HOLD_ZOOM_OFFSET_DEFAULT_MAX = 1.5;
const TWO_D_HOLD_ZOOM_OFFSET_HI_RES_MAX = 6;
const TWO_D_HOLD_ZOOM_OFFSET_STEP = 0.1;
const TWO_D_PINCH_ZOOM_MIN = 1;
const TWO_D_PINCH_ZOOM_MAX = 5;
const TWO_D_MOBILE_SWIPE_MIN_DISTANCE_PX = 44;
const MOBILE_EXPLORE_TOP_OFFSET_PX = 132;
const TERMINAL_FAQ_DROPDOWN_ITEM_FONT_SIZE_PX = 22;
const SLOT_MACHINE_TOTAL_MS = 1000;
const SLOT_MACHINE_STAGGER_MS = 10;
const SLOT_MACHINE_FAST_STEP_MS = 24;
const SLOT_MACHINE_SLOW_STEP_MS = 94;
const SLOT_MACHINE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789+-/*?&%$#@!<>[]{}';

function formatTerminalTimestamp(ts) {
  return new Date(ts).toLocaleString('nb-NO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function makeBinaryRainColumn(symbolCount = 36) {
  return Array.from({ length: symbolCount }, () => (Math.random() > 0.5 ? '1' : '0')).join('\n');
}

function clampIndex(index, length) {
  if (length <= 0) return 0;
  if (index < 0) return 0;
  if (index > length - 1) return length - 1;
  return index;
}

function clampTwoDZoomScale(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return TWO_D_PINCH_ZOOM_MIN;
  return Math.min(TWO_D_PINCH_ZOOM_MAX, Math.max(TWO_D_PINCH_ZOOM_MIN, numeric));
}

function clampTwoDHoldZoomOffset(value, maxOffset = TWO_D_HOLD_ZOOM_OFFSET_DEFAULT_MAX) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(maxOffset, Math.max(TWO_D_HOLD_ZOOM_OFFSET_MIN, numeric));
}

function resolveTouchDistance(touchA, touchB) {
  if (!touchA || !touchB) return 0;
  const dx = Number(touchA.clientX) - Number(touchB.clientX);
  const dy = Number(touchA.clientY) - Number(touchB.clientY);
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return 0;
  return Math.hypot(dx, dy);
}

function resolveOneLineTitleFontSize(title, isFocused) {
  const text = String(title || '');
  const charCount = Math.max(1, text.length);
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1400;
  const availableWidth = Math.max(260, Math.min(900, viewportWidth * 0.96) - 36);
  const avgCharWidthEm = isFocused ? 0.8 : 0.76;
  const estimatedPx = Math.floor(availableWidth / (charCount * avgCharWidthEm));
  const maxPx = isFocused ? 136 : 94;
  const minPx = 10;
  return `${Math.max(minPx, Math.min(maxPx, estimatedPx))}px`;
}

function createFallbackAnswer(question, bot, faqOptions = []) {
  if (!bot) {
    return 'No bot selected yet. Open DEBOTS admin and configure at least one bot.';
  }

  const normalized = String(question || '').trim().toLowerCase();
  const sourceFaqs = Array.isArray(faqOptions) && faqOptions.length
    ? faqOptions
    : (Array.isArray(bot.faqs) ? bot.faqs : []);
  const matchedFaq = sourceFaqs.find((faq) => {
    const faqQuestion = String(faq.question || '').trim().toLowerCase();
    if (!faqQuestion) return false;
    return normalized === faqQuestion || normalized.includes(faqQuestion) || faqQuestion.includes(normalized);
  });

  if (!bot.online) {
    return `${bot.name} is OFFLINE now. Turn ONLINE on to receive FAQ answers.`;
  }

  if (matchedFaq && matchedFaq.answer) {
    return matchedFaq.answer;
  }

  const shortDescription = bot.description || 'This bot is configured for gallery guidance.';
  return `${bot.name}: ${shortDescription} No direct FAQ hit found, but this is a strong next step.`;
}

function randomSlotCharacter() {
  const idx = Math.floor(Math.random() * SLOT_MACHINE_CHARSET.length);
  return SLOT_MACHINE_CHARSET.charAt(idx) || ' ';
}

function primaryImageUrl(item) {
  const candidate = String(
    item?.optimizedSrc
    || item?.optimizedUrl
    || ''
  ).trim();
  return candidate ? assetUrl(candidate) : '';
}

function youtubeIdFromUrl(url) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      const seg = parsed.pathname.split('/').filter(Boolean);
      return seg[0] || '';
    }
    if (parsed.hostname.includes('youtube.com')) {
      const v = parsed.searchParams.get('v');
      if (v) return v;
      const seg = parsed.pathname.split('/').filter(Boolean);
      if (seg[0] === 'embed' && seg[1]) return seg[1];
      if (seg[0] === 'shorts' && seg[1]) return seg[1];
    }
  } catch {
    return '';
  }
  return '';
}

function normalizeMediaTitle(item, fallback = '') {
  const title = String(item?.title || item?.subject || item?.filename || fallback || '').trim();
  return title || fallback;
}

function normalizeMediaDescription(item) {
  return String(item?.description || item?.text || '').trim();
}

function resolveGalleryCardDescription(gallery) {
  return String(gallery?.description || gallery?.beskrivelse || '').trim();
}

function resolveImageLayerSources(mediaItem) {
  const thumbSrc = '';
  const webSrc = String(mediaItem?.optimizedSrc || mediaItem?.optimizedUrl || '').trim();
  const highSrc = '';
  return { thumbSrc, webSrc, highSrc };
}

function resolveMediaPreloadSources(mediaItem) {
  if (!mediaItem || mediaItem.kind !== 'image') return [];
  const { webSrc } = resolveImageLayerSources(mediaItem);
  const unique = new Set();
  [webSrc].forEach((src) => {
    const cleanSrc = String(src || '').trim();
    if (cleanSrc) unique.add(cleanSrc);
  });
  return [...unique];
}

function resolveGalleryPreviewThumbSources(imageItems, maxCount = 4) {
  const ordered = (Array.isArray(imageItems) ? imageItems : [])
    .filter((item) => {
      if (!item) return false;
      const kind = String(item?.kind || 'image').trim().toLowerCase();
      if (kind === 'linebreak') return false;
      if (kind === 'box') return false;
      if (item?.box?.square && kind !== 'image') return false;
      return true;
    })
    .slice()
    .sort((a, b) => {
      const aOrder = Number(a?.order);
      const bOrder = Number(b?.order);
      const safeAOrder = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
      const safeBOrder = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;
      if (safeAOrder !== safeBOrder) return safeAOrder - safeBOrder;
      const aCreated = Number(new Date(a?.createdAt || 0)) || 0;
      const bCreated = Number(new Date(b?.createdAt || 0)) || 0;
      return aCreated - bCreated;
    });

  const thumbs = [];
  const seen = new Set();
  for (const item of ordered) {
    if (thumbs.length >= maxCount) break;
    const raw = String(
      item?.optimizedSrc
      || item?.optimizedUrl
      || ''
    ).trim();
    if (!raw) continue;
    const src = assetUrl(raw);
    if (!src || seen.has(src)) continue;
    seen.add(src);
    thumbs.push(src);
  }

  return {
    thumbs,
    hasMore: ordered.length > thumbs.length,
  };
}

const STRUCTURED_INFO_IGNORED_KEYS = new Set([
  '_id',
  '__v',
  'id',
  'gallery',
  'galleryId',
  'createdAt',
  'updatedAt',
  'url',
  'optimizedUrl',
  'originalUrl',
  'storageUrl',
  'thumbUrl',
  'posterUrl',
]);

function isStructuredInfoPrimitive(value) {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function structuredInfoPrimitiveToText(value) {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nei';
  return '';
}

function humanizeStructuredInfoKey(rawKey, fallback = 'Verdi') {
  const source = String(rawKey || '').trim();
  if (!source) return fallback;

  const normalized = source
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return fallback;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function slugifyRouteSegment(value, fallback = 'item') {
  const source = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return source || fallback;
}

function unpackStructuredInfoPairs(inputValue, maxPairs = 20) {
  const pairs = [];

  const walk = (value, labelPath = '', depth = 0) => {
    if (pairs.length >= maxPairs || value == null || depth > 3) return;

    if (isStructuredInfoPrimitive(value)) {
      const text = structuredInfoPrimitiveToText(value);
      if (!text) return;
      pairs.push({
        label: labelPath || 'Verdi',
        value: text,
      });
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        if (pairs.length >= maxPairs) return;
        const nextLabel = labelPath ? `${labelPath} ${index + 1}` : `${index + 1}`;
        walk(entry, nextLabel, depth + 1);
      });
      return;
    }

    if (typeof value !== 'object') return;

    Object.entries(value).forEach(([rawKey, nestedValue]) => {
      if (pairs.length >= maxPairs) return;
      if (STRUCTURED_INFO_IGNORED_KEYS.has(rawKey)) return;

      const keyLabel = humanizeStructuredInfoKey(rawKey);
      const nextLabel = labelPath ? `${labelPath} ${keyLabel}` : keyLabel;
      walk(nestedValue, nextLabel, depth + 1);
    });
  };

  walk(inputValue, '', 0);
  return pairs;
}

const SlotMachineText = ({ text, triggerKey, style, durationScale = 1, staggerScale = 1, scrambleEvery = 1 }) => {
  const normalizedText = String(text || '');
  const [displayChars, setDisplayChars] = useState(() => normalizedText.split(''));

  useEffect(() => {
    const targetChars = normalizedText.split('');
    if (!targetChars.length) {
      setDisplayChars([]);
      return undefined;
    }

    let cancelled = false;
    const timers = [];
    const safeDurationScale = Number.isFinite(Number(durationScale)) ? Math.max(0.2, Number(durationScale)) : 1;
    const safeStaggerScale = Number.isFinite(Number(staggerScale)) ? Math.max(0.2, Number(staggerScale)) : 1;
    const safeScrambleEvery = Number.isFinite(Number(scrambleEvery))
      ? Math.max(1, Math.floor(Number(scrambleEvery)))
      : 1;
    const totalMs = Math.max(120, Math.round(SLOT_MACHINE_TOTAL_MS * safeDurationScale));
    const staggerMs = Math.max(2, Math.round(SLOT_MACHINE_STAGGER_MS * safeStaggerScale));
    const fastStepMs = Math.max(8, Math.round(SLOT_MACHINE_FAST_STEP_MS * safeDurationScale));
    const slowStepMs = Math.max(fastStepMs + 4, Math.round(SLOT_MACHINE_SLOW_STEP_MS * safeDurationScale));
    const shouldScrambleIndex = (charIndex) => (charIndex % safeScrambleEvery) === 0;

    setDisplayChars(targetChars.map((char, charIndex) => {
      if (/\s/.test(char)) return char;
      return shouldScrambleIndex(charIndex) ? randomSlotCharacter() : char;
    }));

    targetChars.forEach((targetChar, charIndex) => {
      if (/\s/.test(targetChar)) return;
      if (!shouldScrambleIndex(charIndex)) return;

      const startDelay = charIndex * staggerMs;

      const tick = (elapsedMs) => {
        if (cancelled) return;

        if (elapsedMs >= totalMs) {
          setDisplayChars((prev) => {
            const next = prev.slice();
            next[charIndex] = targetChar;
            return next;
          });
          return;
        }

        setDisplayChars((prev) => {
          const next = prev.slice();
          next[charIndex] = randomSlotCharacter();
          return next;
        });

        const progress = Math.min(1, elapsedMs / totalMs);
        const slowdownProgress = progress <= 0.5 ? 0 : ((progress - 0.5) / 0.5);
        const stepMs = progress <= 0.5
          ? fastStepMs
          : Math.round(fastStepMs + ((slowStepMs - fastStepMs) * slowdownProgress));

        const timer = window.setTimeout(() => tick(elapsedMs + stepMs), stepMs);
        timers.push(timer);
      };

      const startTimer = window.setTimeout(() => tick(0), startDelay);
      timers.push(startTimer);
    });

    const settleTimer = window.setTimeout(() => {
      if (cancelled) return;
      setDisplayChars(targetChars);
    }, totalMs + (targetChars.length * staggerMs) + 80);
    timers.push(settleTimer);

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [normalizedText, triggerKey, durationScale, staggerScale, scrambleEvery]);

  return <span style={style}>{displayChars.join('')}</span>;
};

const TerminalPanel = ({
  compact,
  botOptions,
  selectedBotId,
  logs,
  userLabel,
  faqSelection,
  faqOptions,
  onFaqSelect,
  showExploreButton,
  onExploreClick,
  showBackTo2DButton,
  onBackTo2DClick,
  showNewsletterInput,
  newsletterEmail,
  newsletterBusy,
  onNewsletterEmailChange,
  onNewsletterSubmit,
  typewriterNow,
  matrixOnlyMode = false,
  headerScale = 1,
  isExpanded = false,
  onToggleExpand,
  onClose,
}) => {
  const selectedBot = botOptions.find((bot) => bot.id === selectedBotId) || botOptions[0] || null;
  const logsRef = useRef(null);
  const normalizedFaqOptions = useMemo(
    () => (Array.isArray(faqOptions) ? faqOptions.filter((faq) => String(faq?.question || '').trim()) : []),
    [faqOptions]
  );
  const showFaqDropdown = normalizedFaqOptions.length > 0;
  const showMatrixViewport = Boolean(matrixOnlyMode);
  const faqDropdownFontSize = compact
    ? TERMINAL_FAQ_DROPDOWN_ITEM_FONT_SIZE_PX
    : (TERMINAL_FAQ_DROPDOWN_ITEM_FONT_SIZE_PX + 2);
  const logsPaneHeight = compact ? 180 : 260;
  const matrixRainColumns = useMemo(() => {
    const columnCount = compact ? 12 : 16;
    return Array.from({ length: columnCount }, (_, index) => {
      const symbolCount = (compact ? 28 : 38) + Math.floor(Math.random() * 16);
      const fontSize = compact ? (9 + Math.floor(Math.random() * 3)) : (10 + Math.floor(Math.random() * 4));
      const left = columnCount <= 1 ? 50 : Number(((index / (columnCount - 1)) * 100).toFixed(3));
      const baseDuration = 4.2 + (Math.random() * 3.4);
      return {
        id: `binary-rain-${index}`,
        text: makeBinaryRainColumn(symbolCount),
        left,
        duration: Number((baseDuration / 3).toFixed(2)),
        delay: Number(((Math.random() * 5.5) / 3).toFixed(2)),
        opacity: Number((0.18 + (Math.random() * 0.22)).toFixed(2)),
        blur: Math.random() > 0.72 ? 0.6 : 0,
        fontSize,
      };
    });
  }, [compact]);
  const renderExpandToggleIcon = (expanded) => {
    if (expanded) {
      return (
        <span aria-hidden style={{ display: 'inline-block', width: 14, height: 14, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 1, right: 1, bottom: 3, height: 2, borderRadius: 2, background: 'currentColor' }} />
        </span>
      );
    }

    return (
      <span aria-hidden style={{ display: 'inline-block', width: 14, height: 14, position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            right: 1,
            top: 1,
            width: 7,
            height: 7,
            borderTop: '2px solid currentColor',
            borderRight: '2px solid currentColor',
            boxSizing: 'border-box',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 2,
            bottom: 3,
            width: 9,
            height: 2,
            borderRadius: 2,
            background: 'currentColor',
            transform: 'rotate(-45deg)',
            transformOrigin: 'left center',
          }}
        />
      </span>
    );
  };

  useEffect(() => {
    const node = logsRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [logs]);

  return (
    <div
      style={{
        borderRadius: 14,
        border: '1px solid rgba(116, 255, 159, 0.3)',
        background: 'linear-gradient(180deg, rgba(2, 18, 8, 0.96), rgba(1, 9, 4, 0.97))',
        color: '#d4ffd3',
        padding: compact ? 12 : 16,
        display: 'grid',
        gap: compact ? 8 : 10,
        minHeight: compact ? 300 : 480,
        boxShadow: '0 18px 40px rgba(0,0,0,0.55), 0 0 32px rgba(32, 181, 84, 0.16), inset 0 1px 0 rgba(188, 255, 204, 0.1)',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: `${Math.round((compact ? 11 : 12) * headerScale)}px`, opacity: 0.84, letterSpacing: '0.12em', color: '#e4ffe3' }}>DEBOTS TERMINAL</div>
          <div style={{ fontSize: `${Math.round((compact ? 10 : 11) * headerScale)}px`, opacity: 0.68, color: '#93da98' }}>{formatTerminalTimestamp(Date.now())}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onToggleExpand ? (
            <button
              type="button"
              onClick={onToggleExpand}
              title={isExpanded ? 'Minimer terminal' : 'Utvid terminal'}
              aria-label={isExpanded ? 'Minimer terminal' : 'Utvid terminal'}
              style={{
                borderRadius: 8,
                border: '1px solid rgba(144,255,169,0.34)',
                background: isExpanded ? 'rgba(15,64,30,0.88)' : 'rgba(8,28,14,0.84)',
                color: '#d9ffd8',
                width: 34,
                height: 30,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              {renderExpandToggleIcon(isExpanded)}
            </button>
          ) : null}

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: 8,
                border: '1px solid rgba(144,255,169,0.34)',
                background: 'rgba(8,28,14,0.84)',
                color: '#d9ffd8',
                padding: '6px 10px',
                cursor: 'pointer'
              }}
            >
              Lukk
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          borderRadius: 10,
          border: '1px solid rgba(120, 255, 161, 0.24)',
          background: 'radial-gradient(circle at 50% 0%, rgba(24,66,35,0.42), rgba(3,16,8,0.92) 62%, rgba(1,7,3,0.97))',
          minHeight: compact ? 132 : 200,
          maxHeight: logsPaneHeight,
          height: logsPaneHeight,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'inset 0 0 28px rgba(46, 213, 115, 0.12), inset 0 0 58px rgba(0,0,0,0.48)',
          animation: showMatrixViewport ? 'dse-matrix-flicker 7.8s steps(2, end) infinite, dse-matrix-enter-opacity 500ms ease both' : undefined,
        }}
      >
        {showMatrixViewport ? (
          <>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 0,
                background: 'linear-gradient(180deg, rgba(126, 255, 167, 0.06), rgba(10, 37, 17, 0.12))',
              }}
            >
              {matrixRainColumns.map((column) => (
                <span
                  key={column.id}
                  style={{
                    position: 'absolute',
                    left: `${column.left}%`,
                    top: '-130%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'pre',
                    lineHeight: 0.88,
                    letterSpacing: '0.07em',
                    color: 'rgba(155, 255, 169, 0.85)',
                    textShadow: '0 0 8px rgba(77, 255, 127, 0.45)',
                    opacity: column.opacity,
                    fontSize: column.fontSize,
                    filter: column.blur ? `blur(${column.blur}px)` : 'none',
                    animation: `dse-matrix-rain ${column.duration}s linear infinite`,
                    animationDelay: `-${column.delay}s`,
                  }}
                >
                  {column.text}
                </span>
              ))}
            </div>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 1,
                opacity: 0.2,
                backgroundImage: 'repeating-linear-gradient(180deg, rgba(220,255,225,0.22) 0px, rgba(220,255,225,0.22) 1px, rgba(0,0,0,0) 2px, rgba(0,0,0,0) 4px)',
                animation: 'dse-matrix-scanline 6.4s linear infinite',
              }}
            />
          </>
        ) : null}
        <div
          ref={logsRef}
          style={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            overflowY: 'auto',
            padding: '10px 12px',
            lineHeight: 1.5,
            fontSize: compact ? 11 : 12,
            display: 'grid',
            gap: 4,
          }}
        >
          {showMatrixViewport ? null : logs.map((line) => {
            const stamp = formatTerminalTimestamp(line.createdAt);
            const rawText = String(line.text || '');
            const elapsedMs = Math.max(0, Number(typewriterNow || 0) - Number(line.createdAt || 0));
            const typedChars = Math.floor((elapsedMs / 1000) * TERMINAL_TYPEWRITER_CHARS_PER_SEC);
            const typedText = rawText.slice(0, Math.min(rawText.length, Math.max(0, typedChars)));
            const initiated = /3D-Explore initiated/i.test(rawText);

            if (line.speaker === 'user') {
              return (
                <div key={line.id} style={{ color: '#d9ffe2', textShadow: '0 0 8px rgba(68,255,120,0.2)' }}>
                  [{userLabel} | {stamp} | {typedText}]
                </div>
              );
            }

            if (line.speaker === 'bot') {
              return (
                <div key={line.id} style={{ color: '#a6ffb3', textShadow: '0 0 8px rgba(68,255,120,0.28)' }}>
                  [{line.botName || 'DEBOT'} | {stamp} |: {typedText}]
                </div>
              );
            }

            return (
              <div
                key={line.id}
                style={{
                  color: initiated ? '#fff49b' : '#7bff8d',
                  textShadow: '0 0 9px rgba(68,255,120,0.3)',
                  animation: initiated ? 'dse-blink 0.4s steps(2, end) 5' : undefined,
                }}
              >
                [{line.botName || 'SYSTEM'} | {stamp} | {typedText}]
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        {showFaqDropdown || showExploreButton ? (
          <div style={{ display: 'grid', gridTemplateColumns: showFaqDropdown && showExploreButton ? '1fr 1fr' : '1fr', gap: 8 }}>
            {showFaqDropdown ? (
              <select
                value={faqSelection}
                onChange={(event) => onFaqSelect(event.target.value)}
                disabled={!(selectedBot && selectedBot.online)}
                style={{
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: '#09090b',
                  color: '#fff',
                  padding: '8px 9px',
                  fontSize: faqDropdownFontSize,
                  lineHeight: 1.15,
                  opacity: selectedBot && selectedBot.online ? 1 : 0.65,
                }}
              >
                <option value="" style={{ fontSize: faqDropdownFontSize }}>FAQ</option>
                {normalizedFaqOptions.map((faq) => (
                  <option key={faq.question} value={faq.question} style={{ fontSize: faqDropdownFontSize }}>
                    {faq.question}
                  </option>
                ))}
              </select>
            ) : null}

            {showExploreButton && onExploreClick ? (
              <button
                type="button"
                onClick={onExploreClick}
                style={{
                  borderRadius: 5,
                  border: '1px solid rgba(255,144,144,0.68)',
                  background: 'linear-gradient(140deg, rgba(56,8,8,0.98), rgba(110,11,11,0.95) 55%, rgba(42,6,6,0.98))',
                  color: '#fff',
                  width: '100%',
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  padding: compact ? '5px 14px' : '5px 18px',
                  minHeight: Math.round(34 * THREE_D_EXPLORE_BUTTON_HEIGHT_SCALE),
                  cursor: 'pointer',
                  animation: 'dse-terminal-pulse 5s ease-in-out infinite, dse-terminal-pulse-text 5s ease-in-out infinite',
                  whiteSpace: 'nowrap',
                }}
              >
                3D-explore
              </button>
            ) : null}
          </div>
        ) : null}

        {showNewsletterInput ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onNewsletterSubmit();
            }}
            style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}
          >
            <input
              type="email"
              value={newsletterEmail}
              onChange={(event) => onNewsletterEmailChange(event.target.value)}
              placeholder="Nyhetsbrev e-post"
              style={{
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.28)',
                background: '#08080a',
                color: '#fff',
                padding: '8px 9px',
              }}
            />
            <button
              type="submit"
              disabled={newsletterBusy}
              style={{
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.4)',
                background: 'rgba(12,12,16,0.86)',
                color: '#fff',
                padding: '8px 11px',
                cursor: newsletterBusy ? 'not-allowed' : 'pointer',
                opacity: newsletterBusy ? 0.65 : 1,
              }}
            >
              {newsletterBusy ? 'Sender...' : 'Send'}
            </button>
          </form>
        ) : null}

        {showBackTo2DButton && onBackTo2DClick ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onBackTo2DClick}
              style={{ borderRadius: 8, border: '1px solid rgba(252,211,77,0.55)', background: 'rgba(89,62,5,0.62)', color: '#fff7cf', padding: '8px 14px', cursor: 'pointer' }}
            >
              2D
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const GalleryPicker3D = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gallerySlug: gallerySlugParam = '', galleryId: galleryIdParam = '', itemIndex: itemIndexParam = '' } = useParams();

  const [galleries, setGalleries] = useState([]);
  const [galleryCardPreviews, setGalleryCardPreviews] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reveal, setReveal] = useState(false);

  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : true
  );
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1000 : false
  );
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 1260 : false
  );

  const [isAdmin, setIsAdmin] = useState(
    typeof window !== 'undefined' ? Boolean(window.localStorage.getItem('sessionToken')) : false
  );

  const [overviewMode, setOverviewMode] = useState('2d');
  const [showTwoDTerminal, setShowTwoDTerminal] = useState(false);
  const [showThreeDTerminal, setShowThreeDTerminal] = useState(false);
  const [threeDExploreTransitionPhase, setThreeDExploreTransitionPhase] = useState('idle');
  const [focusIndex, setFocusIndex] = useState(0);
  const [focusSmokeTransition, setFocusSmokeTransition] = useState(null);
  const [hoveredTitleIndex, setHoveredTitleIndex] = useState(null);

  const [bots, setBots] = useState([]);
  const [defaulterFaqs, setDefaulterFaqs] = useState(() => getDefaulterTerminalFaqs());
  const [threeDAndTerminalEnabled, setThreeDAndTerminalEnabled] = useState(() => getFrontpageThreeDAndTerminalEnabled());
  const [selectedBotId, setSelectedBotId] = useState('');
  const [faqSelection, setFaqSelection] = useState('');
  const [showTerminalNewsletterInput, setShowTerminalNewsletterInput] = useState(false);
  const [terminalNewsletterEmail, setTerminalNewsletterEmail] = useState('');
  const [terminalNewsletterBusy, setTerminalNewsletterBusy] = useState(false);
  const [terminalLines, setTerminalLines] = useState(() => ([
    {
      id: 'boot-line',
      speaker: 'system',
      text: 'DEBOTS terminal ready.',
      createdAt: Date.now(),
      botName: 'DEBOTS',
    }
  ]));
  const [terminalTypewriterNow, setTerminalTypewriterNow] = useState(() => Date.now());

  const [twoDFocusedGalleryId, setTwoDFocusedGalleryId] = useState('');
  const [twoDMediaItems, setTwoDMediaItems] = useState([]);
  const [twoDMediaIndex, setTwoDMediaIndex] = useState(0);
  const [twoDMediaLoading, setTwoDMediaLoading] = useState(false);
  const [twoDMediaError, setTwoDMediaError] = useState('');
  const [twoDMediaPreviewReady, setTwoDMediaPreviewReady] = useState(false);
  const [twoDMediaAnimationKey, setTwoDMediaAnimationKey] = useState(0);
  const [twoDMediaSlideDirection, setTwoDMediaSlideDirection] = useState(1);
  const [twoDThumbResLoaded, setTwoDThumbResLoaded] = useState(false);
  const [twoDWebResLoaded, setTwoDWebResLoaded] = useState(false);
  const [twoDWebResFailed, setTwoDWebResFailed] = useState(false);
  const [twoDHighResLoaded, setTwoDHighResLoaded] = useState(false);
  const [twoDHighResFailed, setTwoDHighResFailed] = useState(false);
  const [twoDOutgoingMedia, setTwoDOutgoingMedia] = useState(null);
  const [twoDImageAspectRatio, setTwoDImageAspectRatio] = useState(16 / 10);
  const [twoDAllowHighResLoad, setTwoDAllowHighResLoad] = useState(true);
  const [twoDHiResEnabled, setTwoDHiResEnabled] = useState(false);
  const [twoDHoldZoomOffset, setTwoDHoldZoomOffset] = useState(0);
  const [twoDFocusEntryCollapsed, setTwoDFocusEntryCollapsed] = useState(false);
  const [twoDCollapseGate, setTwoDCollapseGate] = useState(false);
  const [isTwoDInfoOpen, setIsTwoDInfoOpen] = useState(false);
  const [twoDPressZoom, setTwoDPressZoom] = useState({ active: false, mediaId: '', x: 50, y: 50, scale: 1 });
  const [twoDGalleryTransitionPhase, setTwoDGalleryTransitionPhase] = useState('idle-list');
  const [twoDTransitionGalleryId, setTwoDTransitionGalleryId] = useState('');
  const [twoDFocusedSwitchPhase, setTwoDFocusedSwitchPhase] = useState('idle');

  const transitionTimerRef = useRef(null);
  const codeBurstTimerRef = useRef(null);
  const smokeTransitionTimerRef = useRef(null);
  const threeDTransitionTimersRef = useRef([]);
  const twoDTerminalAutoOpenTimerRef = useRef(null);
  const faqAnswerTimerRef = useRef(null);
  const preloadedImageSrcRef = useRef(new Set());
  const galleryPreviewRequestRef = useRef(new Set());
  const twoDMediaCacheRef = useRef(new Map());
  const twoDMediaRequestRef = useRef(0);
  const twoDOutgoingMediaTimerRef = useRef(null);
  const previousActiveTwoDMediaRef = useRef(null);
  const twoDGalleryTransitionTimersRef = useRef([]);
  const twoDFocusedSwitchTimersRef = useRef([]);
  const twoDTouchHoldTimerRef = useRef(null);
  const twoDTouchGestureRef = useRef({
    startX: 0,
    startY: 0,
    startedAt: 0,
    holdActive: false,
    pinchBaseDistance: 0,
    pinchBaseScale: 1,
  });
  const lastSyncedRouteRef = useRef('');
  const pendingRouteMediaIdRef = useRef('');
  const pendingRouteMediaIndexRef = useRef(null);
  const previousPathnameRef = useRef(String(location.pathname || ''));

  const selectedBot = useMemo(() => {
    if (!bots.length) return null;
    return bots.find((bot) => bot.id === selectedBotId) || bots[0] || null;
  }, [bots, selectedBotId]);

  const faqOptions = useMemo(
    () => (Array.isArray(defaulterFaqs) ? defaulterFaqs.filter((faq) => String(faq?.question || '').trim()) : []),
    [defaulterFaqs]
  );

  const userLabel = useMemo(() => (isAdmin ? 'Admin' : 'Bruker'), [isAdmin]);
  const isMobileExperience = isMobileViewport;
  const isThreeDTerminalExperienceEnabled = threeDAndTerminalEnabled !== false;

  const focusedTwoDGallery = useMemo(
    () => galleries.find((gallery) => gallery.id === twoDFocusedGalleryId) || null,
    [galleries, twoDFocusedGalleryId]
  );

  const routeGallerySlug = useMemo(
    () => {
      const normalizedParam = String(gallerySlugParam || galleryIdParam || '').trim();
      if (normalizedParam) {
        try {
          return decodeURIComponent(normalizedParam).trim().toLowerCase();
        } catch {
          return normalizedParam.toLowerCase();
        }
      }

      const path = String(location.pathname || '').trim();
      const lowerPath = path.toLowerCase();
      if (!lowerPath.startsWith('/gallery/')) return '';

      const rawSlug = path.slice('/gallery/'.length).split('/')[0] || '';
      const cleanSlug = String(rawSlug || '').trim();
      if (!cleanSlug) return '';

      try {
        return decodeURIComponent(cleanSlug).trim().toLowerCase();
      } catch {
        return cleanSlug.toLowerCase();
      }
    },
    [gallerySlugParam, galleryIdParam, location.pathname]
  );

  const routeMediaId = useMemo(() => {
    const search = new URLSearchParams(location.search || '');
    return String(search.get('item') || '').trim();
  }, [location.search]);

  const routeMediaIndex = useMemo(() => {
    const rawParam = String(itemIndexParam || '').trim();
    if (rawParam) {
      const parsed = Number(rawParam);
      if (Number.isFinite(parsed)) return Math.max(0, Math.floor(parsed));
    }

    const path = String(location.pathname || '').trim();
    const lowerPath = path.toLowerCase();
    if (!lowerPath.startsWith('/gallery/')) return null;

    const parts = path.split('/').filter(Boolean);
    if (parts.length < 3) return null;
    const parsed = Number(parts[2]);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, Math.floor(parsed));
  }, [itemIndexParam, location.pathname]);

  const twoDHoldZoomOffsetMax = twoDHiResEnabled
    ? TWO_D_HOLD_ZOOM_OFFSET_HI_RES_MAX
    : TWO_D_HOLD_ZOOM_OFFSET_DEFAULT_MAX;
  const twoDHoldZoomScale = clampTwoDZoomScale(TWO_D_HOLD_ZOOM_BASE_SCALE + twoDHoldZoomOffset);

  const findGalleryByRouteSlug = useCallback((slug) => {
    const normalizedSlug = String(slug || '').trim().toLowerCase();
    if (!normalizedSlug || !galleries.length) return null;
    return galleries.find((gallery) => {
      const rawId = String(gallery.id || '').trim().toLowerCase();
      const titleSlug = slugifyRouteSegment(gallery.title || gallery.id || 'gallery', 'gallery');
      const idSlug = slugifyRouteSegment(gallery.id || gallery.title || 'gallery', 'gallery');
      return normalizedSlug === rawId || normalizedSlug === titleSlug || normalizedSlug === idSlug;
    }) || null;
  }, [galleries]);

  const activeTwoDMedia = useMemo(() => {
    if (!twoDMediaItems.length) return null;
    const clampedIndex = clampIndex(twoDMediaIndex, twoDMediaItems.length);
    return twoDMediaItems[clampedIndex] || null;
  }, [twoDMediaItems, twoDMediaIndex]);

  const activeTwoDMediaId = useMemo(() => {
    if (!activeTwoDMedia) return '';
    return String(activeTwoDMedia.id || `${twoDFocusedGalleryId}:${twoDMediaIndex}`);
  }, [activeTwoDMedia, twoDFocusedGalleryId, twoDMediaIndex]);

  const activeTwoDInfoRows = useMemo(() => {
    if (!activeTwoDMedia) return [];

    const rows = [];
    const title = String(activeTwoDMedia.title || '').trim() || 'Uten tittel';
    const description = String(activeTwoDMedia.description || '').trim();
    const createdYear = Number(activeTwoDMedia.createdYear);
    const extraInfo = activeTwoDMedia.otherInfo ?? activeTwoDMedia.metadata;

    rows.push({ label: 'Title', value: title });
    if (description) rows.push({ label: 'Description', value: description });
    if (Number.isFinite(createdYear) && createdYear > 0) rows.push({ label: 'Year', value: String(Math.round(createdYear)) });
    if (extraInfo != null && (typeof extraInfo !== 'string' || extraInfo.trim())) rows.push({ label: 'Other info', value: extraInfo });

    return rows.slice(0, 8);
  }, [activeTwoDMedia]);

  const activeTwoDCreatedByEntries = useMemo(() => {
    const source = Array.isArray(activeTwoDMedia?.artCreatedBy) ? activeTwoDMedia.artCreatedBy : [];
    return source.map((entry) => {
      if (!entry || typeof entry === 'string') return String(entry || '').trim();
      const name = String(entry.name || entry.navn || entry.label || entry.title || '').trim();
      const role = String(entry.rolle || entry.role || entry.type || entry.kind || '').trim();
      if (name && role) return `${name} | ${role}`;
      return name || role || '';
    }).filter(Boolean);
  }, [activeTwoDMedia]);

  const activeTwoDParticipantEntries = useMemo(() => {
    const source = Array.isArray(activeTwoDMedia?.deltagere) ? activeTwoDMedia.deltagere : [];
    return source.map((entry) => {
      if (!entry || typeof entry === 'string') return String(entry || '').trim();
      const name = String(entry.name || entry.navn || entry.label || entry.title || '').trim();
      const role = String(entry.rolle || entry.role || entry.type || entry.kind || '').trim();
      if (name && role) return `${name} | ${role}`;
      return name || role || '';
    }).filter(Boolean);
  }, [activeTwoDMedia]);

  const activeTwoDLinkEntries = useMemo(() => {
    const source = Array.isArray(activeTwoDMedia?.links) ? activeTwoDMedia.links : [];
    return source.map((entry, index) => {
      const href = String(entry?.url || entry?.href || entry || '').trim();
      const text = String(entry?.title || entry?.text || entry?.label || '').trim() || `Open Link ${index + 1}`;
      return { href, text, id: String(entry?._id || entry?.id || `link-${index + 1}`) };
    }).filter((entry) => entry.href);
  }, [activeTwoDMedia]);

  const primeImageSource = useCallback((src) => {
    const cleanSrc = String(src || '').trim();
    if (!cleanSrc) return;
    if (preloadedImageSrcRef.current.has(cleanSrc)) return;
    preloadedImageSrcRef.current.add(cleanSrc);

    const image = new Image();
    image.decoding = 'async';
    image.loading = 'eager';
    image.src = cleanSrc;
  }, []);

  const clearTwoDOutgoingMediaTimer = useCallback(() => {
    if (twoDOutgoingMediaTimerRef.current != null) {
      window.clearTimeout(twoDOutgoingMediaTimerRef.current);
      twoDOutgoingMediaTimerRef.current = null;
    }
  }, []);

  const syncGalleryCardPreviewFromMediaItems = useCallback((galleryId, mediaItems) => {
    const safeGalleryId = String(galleryId || '').trim();
    if (!safeGalleryId) return;

    const preview = resolveGalleryPreviewThumbSources(mediaItems, 4);
    preview.thumbs.forEach((src) => primeImageSource(src));

    setGalleryCardPreviews((prev) => {
      const existing = prev[safeGalleryId];
      if (
        existing
        && existing.hasMore === preview.hasMore
        && JSON.stringify(existing.thumbs || []) === JSON.stringify(preview.thumbs || [])
      ) {
        return prev;
      }
      return {
        ...prev,
        [safeGalleryId]: preview,
      };
    });
  }, [primeImageSource]);

  const fetchGalleryCardPreviewThumbs = useCallback(async (galleryId) => {
    const safeGalleryId = String(galleryId || '').trim();
    if (!safeGalleryId) return { thumbs: [], hasMore: false };

    const imageData = await fetchJSON(`/api/gallery/${encodeURIComponent(safeGalleryId)}/images`, { cache: 'no-store' });
    const imageItems = Array.isArray(imageData?.items) ? imageData.items : [];
    return resolveGalleryPreviewThumbSources(imageItems, 4);
  }, []);

  const updateTwoDImageAspectRatioFromElement = useCallback((element) => {
    const width = Number(element?.naturalWidth || element?.videoWidth || 0);
    const height = Number(element?.naturalHeight || element?.videoHeight || 0);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
    const nextRatio = width / height;
    if (!Number.isFinite(nextRatio) || nextRatio <= 0) return;
    setTwoDImageAspectRatio(nextRatio);
  }, []);

  const normalizeTwoDMediaItems = useCallback((galleryId, imageItems, videoItems) => {
    const normalizedImages = (Array.isArray(imageItems) ? imageItems : [])
      .filter((item) => {
        if (!item || item.kind === 'linebreak') return false;
        if (item.kind === 'box') return Boolean(item?.box?.square);
        return true;
      })
      .map((item, index) => {
        const optimizedSrc = primaryImageUrl(item);
        const originalSrcRaw = String(item?.originalUrl || item?.storageUrl || '').trim();
        const originalSrc = originalSrcRaw ? assetUrl(originalSrcRaw) : '';
        const hasSquareBox = Boolean(item?.box?.square);
        const isKanvasItem = hasSquareBox && (item?.kind === 'box' || (!optimizedSrc && !originalSrc));
        const title = normalizeMediaTitle(item, `Media ${index + 1}`);
        const description = normalizeMediaDescription(item);
        const order = Number(item?.order);
        return {
          id: String(item?._id || `${galleryId}:image:${index}`),
          kind: isKanvasItem ? 'box' : 'image',
          galleryId,
          title,
          description,
          optimizedSrc: isKanvasItem ? '' : optimizedSrc,
          originalSrc: isKanvasItem ? '' : originalSrc,
          thumbSrc: '',
          box: hasSquareBox ? item.box : null,
          clickAnimations: hasSquareBox && Array.isArray(item?.box?.clickAnimations) ? item.box.clickAnimations : [],
          createdYear: item?.createdYear,
          artCreatedBy: item?.artCreatedBy,
          deltagere: item?.deltagere,
          links: item?.links,
          tags: item?.tags,
          metadata: item?.metadata,
          otherInfo: item?.otherInfo,
          order: Number.isFinite(order) ? order : (10000 + index),
          createdAt: Number(new Date(item?.createdAt || 0)) || 0,
        };
      })
      .filter((entry) => {
        if (entry.kind === 'box') return Boolean(entry?.box?.square);
        return Boolean(entry.optimizedSrc);
      });

    const normalizedVideos = (Array.isArray(videoItems) ? videoItems : [])
      .map((item, index) => {
        const sourceUrlRaw = String(item?.optimizedUrl || item?.storageUrl || item?.originalUrl || '').trim();
        const youtubeId = String(item?.youtubeId || youtubeIdFromUrl(sourceUrlRaw)).trim();
        const title = normalizeMediaTitle(item, `Video ${index + 1}`);
        const description = normalizeMediaDescription(item);
        const order = Number(item?.order);
        const posterSrcRaw = String(item?.thumbUrl || item?.posterUrl || item?.optimizedUrl || item?.storageUrl || '').trim();
        return {
          id: String(item?._id || `${galleryId}:video:${index}`),
          kind: 'video',
          galleryId,
          title,
          description,
          youtubeId,
          videoSrc: youtubeId ? '' : (sourceUrlRaw ? assetUrl(sourceUrlRaw) : ''),
          posterSrc: posterSrcRaw ? assetUrl(posterSrcRaw) : '',
          createdYear: item?.createdYear,
          artCreatedBy: item?.artCreatedBy,
          deltagere: item?.deltagere,
          links: item?.links,
          tags: item?.tags,
          metadata: item?.metadata,
          otherInfo: item?.otherInfo,
          order: Number.isFinite(order) ? order : (12000 + index),
          createdAt: Number(new Date(item?.createdAt || 0)) || 0,
        };
      })
      .filter((entry) => entry.youtubeId || entry.videoSrc);

    return [...normalizedImages, ...normalizedVideos]
      .sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.createdAt - b.createdAt;
      });
  }, []);

  const fetchTwoDMediaItems = useCallback(async (galleryId) => {
    const safeGalleryId = String(galleryId || '').trim();
    if (!safeGalleryId) return [];

    let imageItems = [];
    let videoItems = [];
    let imageLoadError = null;

    try {
      const imageData = await fetchJSON(`/api/gallery/${encodeURIComponent(safeGalleryId)}/images`, { cache: 'no-store' });
      imageItems = Array.isArray(imageData?.items) ? imageData.items : [];
    } catch (error) {
      imageLoadError = error;
    }

    try {
      const videoData = await fetchJSON(`/api/gallery/${encodeURIComponent(safeGalleryId)}/videos`, { cache: 'no-store' });
      videoItems = Array.isArray(videoData?.items)
        ? videoData.items.filter((entry) => String(entry?.gallery || safeGalleryId) === safeGalleryId)
        : [];
    } catch {
      videoItems = [];
    }

    const normalized = normalizeTwoDMediaItems(safeGalleryId, imageItems, videoItems);
    if (normalized.length > 0) return normalized;

    try {
      const response = await fetch('/galleries.json', { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        const entry = Array.isArray(data?.galleries)
          ? data.galleries.find((gallery) => String(gallery?.id || '') === safeGalleryId)
          : null;
        if (entry && Array.isArray(entry.items)) {
          return normalizeTwoDMediaItems(
            safeGalleryId,
            entry.items.map((url, index) => ({
              _id: `${safeGalleryId}:fallback:${index}`,
              url,
              optimizedUrl: url,
              originalUrl: url,
              order: index,
              title: '',
              description: '',
            })),
            []
          );
        }
      }
    } catch {
      // Fallback is best effort only.
    }

    if (imageLoadError) throw imageLoadError;
    return [];
  }, [normalizeTwoDMediaItems]);

  const loadTwoDGalleryMedia = useCallback(async (galleryId, options = {}) => {
    const safeGalleryId = String(galleryId || '').trim();
    if (!safeGalleryId) return;

    const useCache = options.useCache !== false;
    const preserveMediaIndex = options.preserveMediaIndex === true;
    const requestId = twoDMediaRequestRef.current + 1;
    twoDMediaRequestRef.current = requestId;

    setTwoDFocusedGalleryId(safeGalleryId);
    setTwoDMediaError('');
    setTwoDMediaLoading(true);

    if (!preserveMediaIndex) {
      setTwoDMediaIndex(0);
    }

    const cached = useCache ? twoDMediaCacheRef.current.get(safeGalleryId) : null;
    if (cached) {
      if (requestId !== twoDMediaRequestRef.current) return;
      setTwoDMediaItems(cached);
      setTwoDMediaIndex((prev) => (preserveMediaIndex ? clampIndex(prev, cached.length) : 0));
      syncGalleryCardPreviewFromMediaItems(safeGalleryId, cached);
      setTwoDMediaLoading(false);
      return;
    }

    try {
      const loadedItems = await fetchTwoDMediaItems(safeGalleryId);
      if (requestId !== twoDMediaRequestRef.current) return;
      twoDMediaCacheRef.current.set(safeGalleryId, loadedItems);
      setTwoDMediaItems(loadedItems);
      setTwoDMediaIndex((prev) => (preserveMediaIndex ? clampIndex(prev, loadedItems.length) : 0));
      syncGalleryCardPreviewFromMediaItems(safeGalleryId, loadedItems);
    } catch (loadError) {
      if (requestId !== twoDMediaRequestRef.current) return;
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaError(String(loadError?.message || 'Kunne ikke hente galleri-innhold.'));
    } finally {
      if (requestId === twoDMediaRequestRef.current) {
        setTwoDMediaLoading(false);
      }
    }
  }, [fetchTwoDMediaItems, syncGalleryCardPreviewFromMediaItems]);

  const moveTwoDMedia = useCallback((direction) => {
    pendingRouteMediaIdRef.current = '';
    pendingRouteMediaIndexRef.current = null;
    const dir = direction < 0 ? -1 : 1;
    setTwoDMediaSlideDirection(dir);
    setTwoDMediaIndex((prev) => {
      const total = twoDMediaItems.length;
      if (!total) return 0;
      const baseIndex = clampIndex(prev, total);
      const next = baseIndex + dir;
      if (next < 0) return total - 1;
      if (next >= total) return 0;
      return next;
    });
  }, [twoDMediaItems.length]);

  const clearTwoDFocusedSwitchTimers = useCallback(() => {
    if (!twoDFocusedSwitchTimersRef.current.length) return;
    twoDFocusedSwitchTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    twoDFocusedSwitchTimersRef.current = [];
  }, []);

  const clearTwoDGalleryTransitionTimers = useCallback(() => {
    if (!twoDGalleryTransitionTimersRef.current.length) return;
    twoDGalleryTransitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    twoDGalleryTransitionTimersRef.current = [];
  }, []);

  const queueTwoDGalleryTransitionStep = useCallback((callback, delayMs) => {
    const timerId = window.setTimeout(() => {
      twoDGalleryTransitionTimersRef.current = twoDGalleryTransitionTimersRef.current.filter((id) => id !== timerId);
      callback();
    }, Math.max(0, delayMs));

    twoDGalleryTransitionTimersRef.current.push(timerId);
  }, []);

  const startTwoDFocusTransition = useCallback((galleryId) => {
    const safeGalleryId = String(galleryId || '').trim();
    if (!safeGalleryId) return;

    clearTwoDGalleryTransitionTimers();
    clearTwoDFocusedSwitchTimers();
    setTwoDFocusedSwitchPhase('idle');
    setTwoDFocusEntryCollapsed(false);
    setTwoDAllowHighResLoad(false);
    setIsTwoDInfoOpen(false);
    setTwoDTransitionGalleryId(safeGalleryId);
    setTwoDGalleryTransitionPhase('focus-fade-others');
    loadTwoDGalleryMedia(safeGalleryId);

    queueTwoDGalleryTransitionStep(() => {
      setTwoDGalleryTransitionPhase('focus-expand-picker');
    }, TWO_D_GALLERY_STAGE_MS);

    queueTwoDGalleryTransitionStep(() => {
      setTwoDGalleryTransitionPhase('focus-collapse-overview');
    }, TWO_D_GALLERY_STAGE_MS * 2);

    queueTwoDGalleryTransitionStep(() => {
      setTwoDGalleryTransitionPhase('focused');
      setTwoDFocusEntryCollapsed(true);
      setTwoDCollapseGate(true);
    }, (TWO_D_GALLERY_STAGE_MS * 2) + TWO_D_FOCUSED_SWITCH_COLLAPSE_MS);

    queueTwoDGalleryTransitionStep(() => {
      setTwoDFocusEntryCollapsed(false);
      setTwoDAllowHighResLoad(true);
    }, (TWO_D_GALLERY_STAGE_MS * 2) + (TWO_D_FOCUSED_SWITCH_COLLAPSE_MS * 2));
  }, [clearTwoDGalleryTransitionTimers, clearTwoDFocusedSwitchTimers, loadTwoDGalleryMedia, queueTwoDGalleryTransitionStep]);

  const startTwoDBackToListTransition = useCallback(() => {
    const activeGalleryId = String(twoDFocusedGalleryId || twoDTransitionGalleryId || '').trim();
    const currentPath = String(location.pathname || '').trim().toLowerCase();
    if (currentPath !== '/gallery') {
      lastSyncedRouteRef.current = '/gallery';
      navigate('/gallery');
    }

    if (!activeGalleryId) {
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDTransitionGalleryId('');
      setTwoDFocusedGalleryId('');
      setTwoDFocusEntryCollapsed(false);
      setTwoDCollapseGate(false);
      setTwoDAllowHighResLoad(true);
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaError('');
      return;
    }

    clearTwoDGalleryTransitionTimers();
    clearTwoDFocusedSwitchTimers();
    setTwoDFocusEntryCollapsed(false);
    setTwoDCollapseGate(false);
    setTwoDAllowHighResLoad(true);
    setTwoDFocusedSwitchPhase('idle');
    setTwoDTransitionGalleryId(activeGalleryId);
    setTwoDGalleryTransitionPhase('list-collapse-picker');

    queueTwoDGalleryTransitionStep(() => {
      setTwoDGalleryTransitionPhase('list-fade-in-others');
    }, TWO_D_GALLERY_STAGE_MS);

    queueTwoDGalleryTransitionStep(() => {
      twoDMediaRequestRef.current += 1;
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDTransitionGalleryId('');
      setTwoDFocusedGalleryId('');
      setTwoDFocusEntryCollapsed(false);
      setTwoDCollapseGate(false);
      setTwoDAllowHighResLoad(true);
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaLoading(false);
      setTwoDMediaError('');
      setIsTwoDInfoOpen(false);
    }, TWO_D_GALLERY_STAGE_MS * 2);
  }, [clearTwoDGalleryTransitionTimers, clearTwoDFocusedSwitchTimers, navigate, queueTwoDGalleryTransitionStep, twoDFocusedGalleryId, twoDTransitionGalleryId, location.pathname]);

  useEffect(() => {
    if (!routeGallerySlug || !galleries.length) return;

    if (!isMobileExperience && overviewMode !== '2d') {
      setOverviewMode('2d');
      setShowThreeDTerminal(false);
      setShowTwoDTerminal(false);
    }

    const routeGallery = findGalleryByRouteSlug(routeGallerySlug);
    const fallbackGallery = galleries[0] || null;
    const targetGallery = routeGallery || fallbackGallery;
    if (!targetGallery) return;

    if (twoDFocusedGalleryId === targetGallery.id) return;

    if (isMobileExperience) {
      loadTwoDGalleryMedia(targetGallery.id);
      return;
    }

    if (twoDGalleryTransitionPhase === 'idle-list') {
      startTwoDFocusTransition(targetGallery.id);
      return;
    }

    loadTwoDGalleryMedia(targetGallery.id);
  }, [
    routeGallerySlug,
    galleries,
    findGalleryByRouteSlug,
    overviewMode,
    twoDFocusedGalleryId,
    isMobileExperience,
    twoDGalleryTransitionPhase,
    startTwoDFocusTransition,
    loadTwoDGalleryMedia,
  ]);

  useEffect(() => {
    const navState = location.state;
    const forceOverview = Boolean(
      navState
      && typeof navState === 'object'
      && navState.forceOverview === true
    );
    if (!forceOverview) return;

    if (isMobileExperience) {
      setTwoDFocusedGalleryId('');
      setTwoDTransitionGalleryId('');
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaLoading(false);
      setTwoDMediaError('');
      setTwoDHiResEnabled(false);
      twoDMediaRequestRef.current += 1;
    } else {
      startTwoDBackToListTransition();
    }

    navigate('/gallery', { replace: true, state: null });
  }, [
    location.state,
    isMobileExperience,
    startTwoDBackToListTransition,
    navigate,
  ]);

  useEffect(() => {
    const currentPath = String(location.pathname || '').trim().toLowerCase();
    const previousPath = String(previousPathnameRef.current || '').trim().toLowerCase();
    previousPathnameRef.current = String(location.pathname || '');

    const movedFromGalleryDetailToOverview = currentPath === '/gallery'
      && previousPath.startsWith('/gallery/')
      && !routeGallerySlug;
    if (!movedFromGalleryDetailToOverview) return;

    if (isMobileExperience) {
      setTwoDFocusedGalleryId('');
      setTwoDTransitionGalleryId('');
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaLoading(false);
      setTwoDMediaError('');
      setTwoDHiResEnabled(false);
      twoDMediaRequestRef.current += 1;
      return;
    }

    startTwoDBackToListTransition();
  }, [
    location.pathname,
    routeGallerySlug,
    isMobileExperience,
    startTwoDBackToListTransition,
  ]);

  useEffect(() => {
    pendingRouteMediaIdRef.current = routeMediaId;
    pendingRouteMediaIndexRef.current = Number.isFinite(routeMediaIndex) ? routeMediaIndex : null;
  }, [routeMediaId, routeMediaIndex]);

  useEffect(() => {
    const pendingIndexRaw = pendingRouteMediaIndexRef.current;
    if (!Number.isFinite(pendingIndexRaw) || !twoDMediaItems.length) return;

    const targetIndex = clampIndex(Number(pendingIndexRaw), twoDMediaItems.length);
    if (targetIndex === twoDMediaIndex) {
      pendingRouteMediaIndexRef.current = null;
      return;
    }

    setTwoDMediaSlideDirection(targetIndex > twoDMediaIndex ? 1 : -1);
    setTwoDMediaIndex(targetIndex);
    pendingRouteMediaIndexRef.current = null;
  }, [twoDMediaItems, twoDMediaIndex]);

  useEffect(() => {
    const pendingId = String(pendingRouteMediaIdRef.current || '').trim();
    if (!pendingId || !twoDMediaItems.length) return;

    const matchIndex = twoDMediaItems.findIndex((item) => String(item?.id || '').trim() === pendingId);
    if (matchIndex < 0) {
      pendingRouteMediaIdRef.current = '';
      return;
    }
    if (matchIndex === twoDMediaIndex) {
      pendingRouteMediaIdRef.current = '';
      return;
    }

    setTwoDMediaSlideDirection(matchIndex > twoDMediaIndex ? 1 : -1);
    setTwoDMediaIndex(matchIndex);
    pendingRouteMediaIdRef.current = '';
  }, [twoDMediaItems, twoDMediaIndex]);

  useEffect(() => {
    let active = true;

    async function loadGalleries() {
      setError(null);
      setLoading(true);

      try {
        const cats = await fetchJSON('/api/categories', { cache: 'no-store' });
        let list = (cats.categories || [])
          .filter((cat) => cat.visible !== false)
          .map((cat) => ({
            ...cat,
            id: String(cat.id || ''),
            title: String(cat.title || cat.id || '').trim() || String(cat.id || ''),
            visible: cat.visible !== false,
          }));

        try {
          const agg = await fetchJSON('/api/galleries', { cache: 'no-store' });
          const byId = new Map((agg.galleries || []).map((item) => [item.id, item]));
          list = list.map((gallery) => {
            const merged = byId.get(gallery.id);
            return {
              ...gallery,
              count: typeof merged?.count === 'number' ? merged.count : 0,
            };
          });
        } catch {
          // Non-fatal if aggregate endpoint is unavailable.
        }

        if (!list.length) {
          try {
            const res = await fetch('/galleries.json', { cache: 'no-store' });
            if (res.ok) {
              const data = await res.json();
              const fallback = Array.isArray(data?.galleries)
                ? data.galleries.map((item) => ({
                  id: String(item.id),
                  title: String(item.title || item.id),
                  count: Array.isArray(item.items) ? item.items.length : 0,
                  visible: true,
                }))
                : [];
              if (fallback.length) list = fallback;
            }
          } catch {
            // Ignore fallback error.
          }
        }

        if (active) {
          setGalleries(list);
          setFocusIndex((prev) => clampIndex(prev, list.length));
        }
      } catch (loadError) {
        const msg = String(loadError?.message || 'Kunne ikke hente kategorier');

        try {
          const res = await fetch('/galleries.json', { cache: 'no-store' });
          if (res.ok) {
            const data = await res.json();
            const fallback = Array.isArray(data?.galleries)
              ? data.galleries.map((item) => ({
                id: String(item.id),
                title: String(item.title || item.id),
                count: Array.isArray(item.items) ? item.items.length : 0,
                visible: true,
              }))
              : [];
            if (active) {
              setGalleries(fallback);
              setFocusIndex((prev) => clampIndex(prev, fallback.length));
            }
            if (!fallback.length && active) setError(msg);
          } else if (active) {
            setError(msg);
          }
        } catch {
          if (active) setError(msg);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadGalleries();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!galleries.length) {
      setGalleryCardPreviews({});
      galleryPreviewRequestRef.current.clear();
      return;
    }

    const validIds = new Set(galleries.map((gallery) => String(gallery?.id || '').trim()).filter(Boolean));
    setGalleryCardPreviews((prev) => {
      const nextEntries = Object.entries(prev).filter(([id]) => validIds.has(id));
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [galleries]);

  useEffect(() => {
    if (!galleries.length) return;
    let disposed = false;

    galleries.forEach((gallery) => {
      const safeGalleryId = String(gallery?.id || '').trim();
      if (!safeGalleryId) return;
      if (galleryCardPreviews[safeGalleryId]) return;
      if (galleryPreviewRequestRef.current.has(safeGalleryId)) return;

      const cachedMediaItems = twoDMediaCacheRef.current.get(safeGalleryId);
      if (Array.isArray(cachedMediaItems) && cachedMediaItems.length) {
        syncGalleryCardPreviewFromMediaItems(safeGalleryId, cachedMediaItems);
        return;
      }

      galleryPreviewRequestRef.current.add(safeGalleryId);
      fetchGalleryCardPreviewThumbs(safeGalleryId)
        .then((preview) => {
          if (disposed) return;
          preview.thumbs.forEach((src) => primeImageSource(src));
          setGalleryCardPreviews((prev) => {
            if (prev[safeGalleryId]) return prev;
            return {
              ...prev,
              [safeGalleryId]: preview,
            };
          });
        })
        .catch(() => {
          if (disposed) return;
          setGalleryCardPreviews((prev) => {
            if (prev[safeGalleryId]) return prev;
            return {
              ...prev,
              [safeGalleryId]: {
                thumbs: [],
                hasMore: Number(gallery?.count || 0) > 0,
              },
            };
          });
        })
        .finally(() => {
          galleryPreviewRequestRef.current.delete(safeGalleryId);
        });
    });

    return () => {
      disposed = true;
    };
  }, [
    galleries,
    galleryCardPreviews,
    fetchGalleryCardPreviewThumbs,
    primeImageSource,
    syncGalleryCardPreviewFromMediaItems,
  ]);

  useEffect(() => {
    setReveal(false);
    const timer = window.setTimeout(() => setReveal(true), 700);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const updateViewportState = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsLandscape(width > height);
      setIsMobileViewport(width <= 1000);
      setIsNarrowViewport(width <= 1260);
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    window.addEventListener('orientationchange', updateViewportState);

    return () => {
      window.removeEventListener('resize', updateViewportState);
      window.removeEventListener('orientationchange', updateViewportState);
    };
  }, []);

  useEffect(() => {
    const syncAdmin = () => {
      if (typeof window === 'undefined') return;
      setIsAdmin(Boolean(window.localStorage.getItem('sessionToken')));
    };

    syncAdmin();
    window.addEventListener('storage', syncAdmin);
    window.addEventListener('focus', syncAdmin);

    return () => {
      window.removeEventListener('storage', syncAdmin);
      window.removeEventListener('focus', syncAdmin);
    };
  }, []);

  useEffect(() => {
    const syncBots = () => {
      const nextBots = getDebotConfigs();
      const fallbackBotId = (nextBots.find((bot) => bot.online)?.id) || (nextBots[0]?.id || '');
      setBots(nextBots);
      setSelectedBotId((prev) => (nextBots.some((bot) => bot.id === prev && bot.online) ? prev : fallbackBotId));
    };

    syncBots();
    return subscribeDebotConfigs(syncBots);
  }, []);

  useEffect(() => {
    const syncDefaulterFaqs = () => {
      setDefaulterFaqs(getDefaulterTerminalFaqs());
    };

    syncDefaulterFaqs();
    return subscribeDefaulterTerminalFaqs(syncDefaulterFaqs);
  }, []);

  useEffect(() => {
    const syncFrontpageThreeDFlag = (nextValue) => {
      if (typeof nextValue === 'boolean') {
        setThreeDAndTerminalEnabled(nextValue);
      } else {
        setThreeDAndTerminalEnabled(getFrontpageThreeDAndTerminalEnabled());
      }
    };

    syncFrontpageThreeDFlag();
    return subscribeFrontpageThreeDAndTerminalEnabled(syncFrontpageThreeDFlag);
  }, []);

  useEffect(() => {
    if (!faqSelection) return;
    const exists = faqOptions.some((faq) => faq.question === faqSelection);
    if (!exists) setFaqSelection('');
  }, [faqOptions, faqSelection]);

  useEffect(() => {
    setFocusIndex((prev) => clampIndex(prev, galleries.length));
  }, [galleries.length]);

  useEffect(() => {
    if (!galleries.length) {
      clearTwoDGalleryTransitionTimers();
      clearTwoDFocusedSwitchTimers();
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDTransitionGalleryId('');
      setTwoDFocusEntryCollapsed(false);
      setTwoDCollapseGate(false);
      setTwoDAllowHighResLoad(true);
      setTwoDFocusedSwitchPhase('idle');
      setTwoDFocusedGalleryId('');
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
      setTwoDMediaLoading(false);
      setTwoDMediaError('');
      return;
    }

    const hasFocusedGallery = galleries.some((gallery) => gallery.id === twoDFocusedGalleryId);
    if (isMobileExperience) {
      const onOverviewRoute = String(location.pathname || '').trim().toLowerCase() === '/gallery' && !routeGallerySlug;
      if (onOverviewRoute) {
        return;
      }

      if (!hasFocusedGallery) {
        loadTwoDGalleryMedia(galleries[0].id);
      }
      return;
    }

    if (twoDFocusedGalleryId && !hasFocusedGallery) {
      clearTwoDGalleryTransitionTimers();
      clearTwoDFocusedSwitchTimers();
      setTwoDGalleryTransitionPhase('idle-list');
      setTwoDTransitionGalleryId('');
      setTwoDFocusEntryCollapsed(false);
      setTwoDCollapseGate(false);
      setTwoDAllowHighResLoad(true);
      setTwoDFocusedSwitchPhase('idle');
      setTwoDFocusedGalleryId('');
      setTwoDMediaItems([]);
      setTwoDMediaIndex(0);
    }
  }, [
    galleries,
    isMobileExperience,
    twoDFocusedGalleryId,
    loadTwoDGalleryMedia,
    clearTwoDGalleryTransitionTimers,
    clearTwoDFocusedSwitchTimers,
    location.pathname,
    routeGallerySlug,
  ]);

  useEffect(() => {
    setTwoDMediaAnimationKey((prev) => prev + 1);
  }, [activeTwoDMediaId]);

  useEffect(() => {
    const previousMedia = previousActiveTwoDMediaRef.current;
    const nextMedia = activeTwoDMedia || null;

    if (
      previousMedia
      && nextMedia
      && String(previousMedia.id || '') !== String(nextMedia.id || '')
      && previousMedia.kind === 'image'
    ) {
      setTwoDOutgoingMedia(previousMedia);
      clearTwoDOutgoingMediaTimer();
      twoDOutgoingMediaTimerRef.current = window.setTimeout(() => {
        setTwoDOutgoingMedia(null);
        twoDOutgoingMediaTimerRef.current = null;
      }, TWO_D_MEDIA_TRANSITION_MS);
    }

    if (!nextMedia) {
      clearTwoDOutgoingMediaTimer();
      setTwoDOutgoingMedia(null);
    }

    previousActiveTwoDMediaRef.current = nextMedia;
  }, [activeTwoDMediaId, activeTwoDMedia, clearTwoDOutgoingMediaTimer]);

  useEffect(() => () => {
    clearTwoDOutgoingMediaTimer();
  }, [clearTwoDOutgoingMediaTimer]);

  useEffect(() => {
    if (!activeTwoDMedia) {
      setTwoDMediaPreviewReady(true);
      setTwoDThumbResLoaded(false);
      setTwoDWebResLoaded(false);
      setTwoDWebResFailed(false);
      setTwoDHighResLoaded(false);
      setTwoDHighResFailed(false);
      return undefined;
    }

    setTwoDMediaPreviewReady(false);
    setTwoDThumbResLoaded(false);
    setTwoDWebResLoaded(false);
    setTwoDWebResFailed(false);
    setTwoDHighResLoaded(false);
    setTwoDHighResFailed(false);

    if (activeTwoDMedia.kind === 'image') {
      const { thumbSrc, webSrc, highSrc } = resolveImageLayerSources(activeTwoDMedia);
      if (!thumbSrc && !webSrc && !highSrc) {
        setTwoDMediaPreviewReady(true);
        setTwoDThumbResLoaded(true);
        setTwoDWebResLoaded(true);
        setTwoDHighResLoaded(true);
        return undefined;
      }

      resolveMediaPreloadSources(activeTwoDMedia).forEach((src) => primeImageSource(src));

      if (!thumbSrc) setTwoDThumbResLoaded(true);
      if (!webSrc) setTwoDWebResLoaded(true);
      if (!highSrc) setTwoDHighResLoaded(true);

      const timerId = window.setTimeout(() => {
        setTwoDMediaPreviewReady(true);
      }, 1400);

      return () => {
        window.clearTimeout(timerId);
      };
    }

    if (activeTwoDMedia.kind === 'video' && !activeTwoDMedia.youtubeId && !activeTwoDMedia.videoSrc) {
      setTwoDMediaPreviewReady(true);
      return undefined;
    }

    if (activeTwoDMedia.kind === 'box') {
      setTwoDMediaPreviewReady(true);
      const timerId = window.setTimeout(() => {
        setTwoDMediaPreviewReady(true);
      }, 900);
      return () => {
        window.clearTimeout(timerId);
      };
    }

    if (activeTwoDMedia.kind === 'video' && activeTwoDMedia.youtubeId) {
      const timerId = window.setTimeout(() => {
        setTwoDMediaPreviewReady(true);
      }, 850);
      return () => {
        window.clearTimeout(timerId);
      };
    }

    return undefined;
  }, [activeTwoDMediaId, activeTwoDMedia, primeImageSource]);

  useEffect(() => {
    if (!twoDMediaItems.length) return;

    const total = twoDMediaItems.length;
    const activeIndex = clampIndex(twoDMediaIndex, total);
    const activeMedia = twoDMediaItems[activeIndex] || null;
    const prevMedia = twoDMediaItems[(activeIndex - 1 + total) % total] || null;
    const nextMedia = twoDMediaItems[(activeIndex + 1) % total] || null;

    [activeMedia, prevMedia, nextMedia].forEach((item) => {
      resolveMediaPreloadSources(item).forEach((src) => primeImageSource(src));
    });
  }, [activeTwoDMediaId, twoDMediaItems, twoDMediaIndex, primeImageSource]);

  useEffect(() => {
    if (twoDMediaPreviewReady && twoDCollapseGate) {
      setTwoDCollapseGate(false);
    }
  }, [twoDMediaPreviewReady, twoDCollapseGate]);

  useEffect(() => {
    if (!twoDCollapseGate) return;
    if (twoDMediaLoading) return;
    setTwoDCollapseGate(false);
  }, [twoDCollapseGate, twoDMediaLoading]);

  useEffect(() => {
    if (!activeTwoDMedia || activeTwoDMedia.kind !== 'image') {
      setTwoDHighResLoaded(true);
      setTwoDHighResFailed(false);
      return;
    }

    if (twoDWebResFailed) {
      setTwoDHighResLoaded(false);
      return;
    }

    if (!twoDAllowHighResLoad || !twoDMediaPreviewReady || !twoDWebResLoaded) {
      return;
    }

    const webSrc = String(activeTwoDMedia.optimizedSrc || activeTwoDMedia.originalSrc || activeTwoDMedia.thumbSrc || '').trim();
    const highSrc = String(activeTwoDMedia.originalSrc || webSrc).trim();
    if (!highSrc || highSrc === webSrc) {
      setTwoDHighResLoaded(true);
      setTwoDHighResFailed(false);
    }
  }, [activeTwoDMediaId, activeTwoDMedia, twoDAllowHighResLoad, twoDMediaPreviewReady, twoDWebResLoaded, twoDWebResFailed]);

  const clearTwoDTouchHoldTimer = useCallback(() => {
    if (twoDTouchHoldTimerRef.current != null) {
      window.clearTimeout(twoDTouchHoldTimerRef.current);
      twoDTouchHoldTimerRef.current = null;
    }
    twoDTouchGestureRef.current.holdActive = false;
  }, []);

  const releaseTwoDPressZoom = useCallback(() => {
    clearTwoDTouchHoldTimer();
    setTwoDPressZoom((prev) => {
      if (!prev.active && prev.scale === 1) return prev;
      return { active: false, mediaId: prev.mediaId, x: prev.x, y: prev.y, scale: 1 };
    });
  }, [clearTwoDTouchHoldTimer]);

  const updateTwoDPressZoom = useCallback((event, mediaId, activate = false, options = {}) => {
    if (!mediaId) return;
    const element = event.currentTarget;
    if (!element) return;
    const point = event.touches?.[0] || event.changedTouches?.[0] || event;
    const clientX = Number(point?.clientX);
    const clientY = Number(point?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

    setTwoDPressZoom((prev) => {
      if (!activate && (!prev.active || prev.mediaId !== mediaId)) {
        return prev;
      }

      const defaultScale = activate
        ? ((prev.active && prev.mediaId === mediaId) ? prev.scale : twoDHoldZoomScale)
        : prev.scale;
      const nextScale = clampTwoDZoomScale(options.scale ?? defaultScale);

      return {
        active: true,
        mediaId,
        x,
        y,
        scale: nextScale,
      };
    });
  }, [twoDHoldZoomScale]);

  const getTwoDPressZoomHandlers = useCallback((mediaId, options = {}) => {
    const allowPressZoom = options.allowPressZoom !== false;

    return {
      onMouseDown: (event) => {
        if (!allowPressZoom) return;
        if (Number(event.button) !== 0) return;
        updateTwoDPressZoom(event, mediaId, true, { scale: twoDHoldZoomScale });
      },
      onMouseMove: (event) => {
        if (!allowPressZoom) return;
        updateTwoDPressZoom(event, mediaId, false);
      },
      onMouseUp: releaseTwoDPressZoom,
      onMouseLeave: () => {
        releaseTwoDPressZoom();
      },
      onTouchStart: (event) => {
        const element = event.currentTarget;
        if (!element) return;

        const touches = event.touches;
        if (touches.length >= 2) {
          clearTwoDTouchHoldTimer();
          if (!allowPressZoom) {
            twoDTouchGestureRef.current.holdActive = false;
            return;
          }

          const touchA = touches[0];
          const touchB = touches[1];
          const centerX = (Number(touchA.clientX) + Number(touchB.clientX)) / 2;
          const centerY = (Number(touchA.clientY) + Number(touchB.clientY)) / 2;
          const pinchDistance = resolveTouchDistance(touchA, touchB);
          const baseScale = twoDPressZoom.active && twoDPressZoom.mediaId === mediaId
            ? twoDPressZoom.scale
            : 1.6;

          twoDTouchGestureRef.current = {
            startX: centerX,
            startY: centerY,
            startedAt: Date.now(),
            holdActive: true,
            pinchBaseDistance: Math.max(1, pinchDistance),
            pinchBaseScale: clampTwoDZoomScale(baseScale),
          };

          updateTwoDPressZoom(
            { currentTarget: element, touches: [{ clientX: centerX, clientY: centerY }] },
            mediaId,
            true,
            { scale: clampTwoDZoomScale(baseScale) }
          );
          return;
        }

        const touch = touches[0];
        if (!touch) return;
        const startX = Number(touch.clientX);
        const startY = Number(touch.clientY);

        twoDTouchGestureRef.current = {
          startX,
          startY,
          startedAt: Date.now(),
          holdActive: false,
          pinchBaseDistance: 0,
          pinchBaseScale: 1,
        };

        clearTwoDTouchHoldTimer();
        if (!allowPressZoom) return;

        twoDTouchHoldTimerRef.current = window.setTimeout(() => {
          twoDTouchGestureRef.current.holdActive = true;
          updateTwoDPressZoom(
            { currentTarget: element, touches: [{ clientX: startX, clientY: startY }] },
            mediaId,
            true,
            { scale: twoDHoldZoomScale }
          );
          twoDTouchHoldTimerRef.current = null;
        }, TWO_D_HOLD_TO_ZOOM_DELAY_MS);
      },
      onTouchMove: (event) => {
        const element = event.currentTarget;
        if (!element) return;

        const touches = event.touches;
        if (touches.length >= 2) {
          clearTwoDTouchHoldTimer();
          if (!allowPressZoom) return;

          const touchA = touches[0];
          const touchB = touches[1];
          const centerX = (Number(touchA.clientX) + Number(touchB.clientX)) / 2;
          const centerY = (Number(touchA.clientY) + Number(touchB.clientY)) / 2;
          const pinchDistance = resolveTouchDistance(touchA, touchB);
          const baseDistance = Math.max(1, twoDTouchGestureRef.current.pinchBaseDistance || pinchDistance);
          const baseScale = clampTwoDZoomScale(twoDTouchGestureRef.current.pinchBaseScale || 1.6);
          const scaleRatio = pinchDistance > 0 ? pinchDistance / baseDistance : 1;
          const scale = clampTwoDZoomScale(baseScale * scaleRatio);

          twoDTouchGestureRef.current.holdActive = true;

          updateTwoDPressZoom(
            { currentTarget: element, touches: [{ clientX: centerX, clientY: centerY }] },
            mediaId,
            true,
            { scale }
          );
          event.preventDefault();
          return;
        }

        const touch = touches[0];
        if (!touch) return;

        const dx = Number(touch.clientX) - twoDTouchGestureRef.current.startX;
        const dy = Number(touch.clientY) - twoDTouchGestureRef.current.startY;
        if (!twoDTouchGestureRef.current.holdActive && (Math.abs(dx) > 14 || Math.abs(dy) > 14)) {
          clearTwoDTouchHoldTimer();
        }

        if (allowPressZoom && twoDPressZoom.active && twoDPressZoom.mediaId === mediaId) {
          updateTwoDPressZoom(event, mediaId, false);
          event.preventDefault();
        }
      },
      onTouchEnd: (event) => {
        const gesture = twoDTouchGestureRef.current;
        const changedTouch = event.changedTouches?.[0];

        if (!gesture.holdActive && changedTouch && twoDMediaItems.length > 1) {
          const dx = Number(changedTouch.clientX) - gesture.startX;
          const dy = Number(changedTouch.clientY) - gesture.startY;
          const elapsed = Date.now() - gesture.startedAt;
          const isSwipe = Math.abs(dx) >= TWO_D_MOBILE_SWIPE_MIN_DISTANCE_PX
            && Math.abs(dx) > (Math.abs(dy) * 1.1)
            && elapsed < 700;

          if (isSwipe) {
            moveTwoDMedia(dx < 0 ? 1 : -1);
          }
        }

        clearTwoDTouchHoldTimer();
        releaseTwoDPressZoom();
      },
      onTouchCancel: () => {
        clearTwoDTouchHoldTimer();
        releaseTwoDPressZoom();
      },
      onContextMenu: (event) => event.preventDefault(),
    };
  }, [
    clearTwoDTouchHoldTimer,
    moveTwoDMedia,
    releaseTwoDPressZoom,
    twoDMediaItems.length,
    twoDPressZoom.active,
    twoDPressZoom.mediaId,
    twoDPressZoom.scale,
    twoDHoldZoomScale,
    updateTwoDPressZoom,
  ]);

  useEffect(() => {
    setTwoDHoldZoomOffset((prev) => clampTwoDHoldZoomOffset(prev, twoDHoldZoomOffsetMax));
  }, [twoDHoldZoomOffsetMax]);

  useEffect(() => {
    if (!twoDPressZoom.active) return () => {};

    window.addEventListener('mouseup', releaseTwoDPressZoom);
    window.addEventListener('touchend', releaseTwoDPressZoom);
    window.addEventListener('touchcancel', releaseTwoDPressZoom);
    window.addEventListener('blur', releaseTwoDPressZoom);

    return () => {
      window.removeEventListener('mouseup', releaseTwoDPressZoom);
      window.removeEventListener('touchend', releaseTwoDPressZoom);
      window.removeEventListener('touchcancel', releaseTwoDPressZoom);
      window.removeEventListener('blur', releaseTwoDPressZoom);
    };
  }, [twoDPressZoom.active, releaseTwoDPressZoom]);

  useEffect(() => {
    releaseTwoDPressZoom();
  }, [activeTwoDMediaId, releaseTwoDPressZoom]);

  useEffect(() => {
    if (!showTwoDTerminal && !showThreeDTerminal) return undefined;
    const timer = window.setInterval(() => {
      setTerminalTypewriterNow(Date.now());
    }, 28);

    return () => {
      window.clearInterval(timer);
    };
  }, [showTwoDTerminal, showThreeDTerminal, terminalLines.length]);

  const appendTerminalLine = (speaker, text, botName) => {
    const createdAt = Date.now();
    setTerminalLines((prev) => {
      const next = [
        ...prev,
        {
          id: `${createdAt.toString(36)}-${Math.round(Math.random() * 1e7).toString(36)}`,
          speaker,
          text,
          createdAt,
          botName,
        }
      ];
      return next.slice(-MAX_TERMINAL_LINES);
    });
    setTerminalTypewriterNow(createdAt);
  };

  const logTerminalCommandExecution = (actionLabel, commandCode, executionLine) => {
    const actionText = String(actionLabel || '').trim();
    const commandText = String(commandCode || '').trim();
    const executionText = String(executionLine || '').trim();

    if (actionText) appendTerminalLine('user', actionText);
    if (commandText) appendTerminalLine('system', `CODE::${commandText}`, 'DEBOTS');
    if (executionText) appendTerminalLine('system', `EXEC::${executionText}`, 'DEBOTS');
  };

  const clearFaqAnswerTimer = useCallback(() => {
    if (faqAnswerTimerRef.current != null) {
      window.clearTimeout(faqAnswerTimerRef.current);
      faqAnswerTimerRef.current = null;
    }
  }, []);

  const askQuestion = (rawQuestion) => {
    const question = String(rawQuestion || '').trim();
    if (!question) return;

    clearFaqAnswerTimer();
    const activeBot = selectedBot || null;
    const botName = activeBot ? activeBot.name : 'DEBOT';

    appendTerminalLine('user', question);
    appendTerminalLine('system', `CODE::FAQ.SEARCH chars=${question.length}`, 'DEBOTS');
    appendTerminalLine('system', 'EXEC::RUN FAQ fallback pipeline', 'DEBOTS');

    const answer = createFallbackAnswer(question, activeBot, faqOptions);
    const questionTypeDurationMs = Math.max(
      300,
      Math.ceil((question.length / TERMINAL_TYPEWRITER_CHARS_PER_SEC) * 1000) + 140
    );

    faqAnswerTimerRef.current = window.setTimeout(() => {
      appendTerminalLine('bot', answer, botName);
      faqAnswerTimerRef.current = null;
    }, questionTypeDurationMs);

    setFaqSelection('');
  };

  const handleFaqSelect = (question) => {
    setFaqSelection(question);
    if (!question) {
      clearFaqAnswerTimer();
      return;
    }
    askQuestion(question);
  };

  const clearTwoDTerminalAutoOpenTimer = useCallback(() => {
    if (twoDTerminalAutoOpenTimerRef.current != null) {
      window.clearTimeout(twoDTerminalAutoOpenTimerRef.current);
      twoDTerminalAutoOpenTimerRef.current = null;
    }
  }, []);

  const clearThreeDTransitionTimers = useCallback(() => {
    if (!threeDTransitionTimersRef.current.length) return;
    threeDTransitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    threeDTransitionTimersRef.current = [];
  }, []);

  const queueThreeDTransitionStep = useCallback((callback, delayMs) => {
    const timerId = window.setTimeout(() => {
      threeDTransitionTimersRef.current = threeDTransitionTimersRef.current.filter((id) => id !== timerId);
      callback();
    }, Math.max(0, delayMs));

    threeDTransitionTimersRef.current.push(timerId);
  }, []);

  const submitNewsletterSignupFromTerminal = async () => {
    const botName = selectedBot ? selectedBot.name : 'DEBOT';
    const cleaned = String(terminalNewsletterEmail || '').trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      appendTerminalLine('bot', 'Skriv inn en gyldig e-postadresse for nyhetsbrev.', botName);
      return;
    }

    appendTerminalLine('user', `Nyhetsbrev e-post: ${cleaned}`);
    appendTerminalLine('system', 'Sender nyhetsbrev-registrering...', 'DEBOTS');

    setTerminalNewsletterBusy(true);
    try {
      await fetchJSON('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, category: 'Fans' }),
      });
      appendTerminalLine('bot', 'Welcome mail sent! Sjekk innboksen din for videre instruksjoner.', botName);
      setTerminalNewsletterEmail('');
      setShowTerminalNewsletterInput(false);
    } catch {
      appendTerminalLine('bot', 'Kunne ikke melde paa naa. Proev igjen litt senere.', botName);
    } finally {
      setTerminalNewsletterBusy(false);
    }
  };

  const clearTransitionTimers = useCallback(() => {
    if (transitionTimerRef.current != null) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    if (codeBurstTimerRef.current != null) {
      window.clearTimeout(codeBurstTimerRef.current);
      codeBurstTimerRef.current = null;
    }

    clearThreeDTransitionTimers();

    clearTwoDTerminalAutoOpenTimer();

    clearSmokeTransitionTimer();
  }, [clearThreeDTransitionTimers, clearTwoDTerminalAutoOpenTimer]);

  useEffect(() => {
    if (isThreeDTerminalExperienceEnabled) return;

    clearTransitionTimers();
    clearTwoDGalleryTransitionTimers();
    clearTwoDFocusedSwitchTimers();
    setShowTwoDTerminal(false);
    setShowThreeDTerminal(false);
    setThreeDExploreTransitionPhase('idle');

    if (overviewMode !== '2d') {
      setOverviewMode('2d');
    }
  }, [
    isThreeDTerminalExperienceEnabled,
    clearTransitionTimers,
    clearTwoDGalleryTransitionTimers,
    clearTwoDFocusedSwitchTimers,
    overviewMode,
  ]);

  const beginThreeDExplore = () => {
    if (!isThreeDTerminalExperienceEnabled) return;
    if (isMobileExperience) return;
    if (overviewMode !== '2d') return;

    // Entering 3D always starts by presenting the first gallery item.
    setFocusSmokeTransition(null);
    setHoveredTitleIndex(null);
    setFocusIndex(0);
    setShowThreeDTerminal(false);
    setThreeDExploreTransitionPhase('code-rain');

    clearTwoDTerminalAutoOpenTimer();

    clearTransitionTimers();
    setOverviewMode('to3d');
    setThreeDExploreTransitionPhase('code-rain');

    const whiteBlurDelayMs = THREE_D_TRANSITION_CODE_MS;
    const fadeOutDelayMs = whiteBlurDelayMs + THREE_D_TRANSITION_WHITE_BLUR_MS;
    const arrivalWhiteDelayMs = fadeOutDelayMs + THREE_D_TRANSITION_FADE_OUT_MS;
    const arrivalClearDelayMs = arrivalWhiteDelayMs + THREE_D_TRANSITION_ARRIVAL_WHITE_MS;
    const finishDelayMs = TRANSITION_TO_3D_MS;

    queueThreeDTransitionStep(() => {
      setThreeDExploreTransitionPhase('white-blur');
    }, whiteBlurDelayMs);

    queueThreeDTransitionStep(() => {
      setThreeDExploreTransitionPhase('fade-out');
    }, fadeOutDelayMs);

    queueThreeDTransitionStep(() => {
      setThreeDExploreTransitionPhase('arrival-white');
    }, arrivalWhiteDelayMs);

    queueThreeDTransitionStep(() => {
      setThreeDExploreTransitionPhase('arrival-clear');
    }, arrivalClearDelayMs);

    queueThreeDTransitionStep(() => {
      setOverviewMode('3d');
      setThreeDExploreTransitionPhase('idle');
      setShowThreeDTerminal(false);
      setShowTwoDTerminal(false);
    }, finishDelayMs);
  };

  const shutdownTo2D = () => {
    if (overviewMode !== '3d') return;

    appendTerminalLine('system', 'shutdown', 'DEBOTS');
    setOverviewMode('to2d');

    clearTransitionTimers();
    transitionTimerRef.current = window.setTimeout(() => {
      setShowThreeDTerminal(false);
      setOverviewMode('2d');
      transitionTimerRef.current = null;
    }, TRANSITION_TO_2D_MS);
  };

  useEffect(() => {
    if (!isMobileExperience) return;

    if (transitionTimerRef.current != null) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    if (codeBurstTimerRef.current != null) {
      window.clearTimeout(codeBurstTimerRef.current);
      codeBurstTimerRef.current = null;
    }

    if (smokeTransitionTimerRef.current != null) {
      window.clearTimeout(smokeTransitionTimerRef.current);
      smokeTransitionTimerRef.current = null;
    }

    clearTwoDTerminalAutoOpenTimer();
    clearThreeDTransitionTimers();
    clearTwoDGalleryTransitionTimers();
    clearTwoDFocusedSwitchTimers();
    setTwoDGalleryTransitionPhase('idle-list');
    setTwoDTransitionGalleryId('');
    setTwoDFocusEntryCollapsed(false);
    setTwoDCollapseGate(false);
    setTwoDAllowHighResLoad(true);
    setTwoDFocusedSwitchPhase('idle');
    setThreeDExploreTransitionPhase('idle');
    setShowTwoDTerminal(false);
    setShowThreeDTerminal(false);
    setOverviewMode('2d');
  }, [isMobileExperience, clearTwoDTerminalAutoOpenTimer, clearThreeDTransitionTimers, clearTwoDGalleryTransitionTimers, clearTwoDFocusedSwitchTimers]);

  useEffect(() => () => {
    if (transitionTimerRef.current != null) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    if (codeBurstTimerRef.current != null) {
      window.clearTimeout(codeBurstTimerRef.current);
      codeBurstTimerRef.current = null;
    }

    if (smokeTransitionTimerRef.current != null) {
      window.clearTimeout(smokeTransitionTimerRef.current);
      smokeTransitionTimerRef.current = null;
    }

    if (twoDTerminalAutoOpenTimerRef.current != null) {
      window.clearTimeout(twoDTerminalAutoOpenTimerRef.current);
      twoDTerminalAutoOpenTimerRef.current = null;
    }

    if (faqAnswerTimerRef.current != null) {
      window.clearTimeout(faqAnswerTimerRef.current);
      faqAnswerTimerRef.current = null;
    }

    if (twoDGalleryTransitionTimersRef.current.length) {
      twoDGalleryTransitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      twoDGalleryTransitionTimersRef.current = [];
    }

    if (twoDFocusedSwitchTimersRef.current.length) {
      twoDFocusedSwitchTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      twoDFocusedSwitchTimersRef.current = [];
    }

    if (threeDTransitionTimersRef.current.length) {
      threeDTransitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      threeDTransitionTimersRef.current = [];
    }

    if (twoDTouchHoldTimerRef.current != null) {
      window.clearTimeout(twoDTouchHoldTimerRef.current);
      twoDTouchHoldTimerRef.current = null;
    }

    twoDMediaRequestRef.current += 1;
  }, []);

  function clearSmokeTransitionTimer() {
    if (smokeTransitionTimerRef.current != null) {
      window.clearTimeout(smokeTransitionTimerRef.current);
      smokeTransitionTimerRef.current = null;
    }
  }

  const moveFocusTo = (nextIndex, directionHint = 0) => {
    const clamped = clampIndex(nextIndex, galleries.length);
    if (clamped === focusIndex) return;

    const derivedDirection = directionHint === 0
      ? (clamped > focusIndex ? 1 : -1)
      : (directionHint > 0 ? 1 : -1);

    setFocusSmokeTransition({
      fromIndex: focusIndex,
      toIndex: clamped,
      direction: derivedDirection,
      startedAt: Date.now(),
    });

    clearSmokeTransitionTimer();
    smokeTransitionTimerRef.current = window.setTimeout(() => {
      setFocusSmokeTransition(null);
      smokeTransitionTimerRef.current = null;
    }, SMOKE_TRANSITION_MS);

    setFocusIndex(clamped);
  };

  const goToGallery = (galleryId) => {
    const target = galleries.find((gallery) => String(gallery?.id || '') === String(galleryId || '').trim()) || null;
    const slug = slugifyRouteSegment(target?.title || target?.id || galleryId || 'gallery', 'gallery');
    navigate(`/gallery/${encodeURIComponent(slug)}`);
  };

  const requireRotation = false;
  const inThreeDTransition = isThreeDTerminalExperienceEnabled && !isMobileExperience && overviewMode === 'to3d';
  const isThreeDTransitionCode = inThreeDTransition && threeDExploreTransitionPhase === 'code-rain';
  const isThreeDTransitionWhiteBlur = inThreeDTransition && threeDExploreTransitionPhase === 'white-blur';
  const isThreeDTransitionFadeOut = inThreeDTransition && threeDExploreTransitionPhase === 'fade-out';
  const isThreeDTransitionArrivalWhite = inThreeDTransition && threeDExploreTransitionPhase === 'arrival-white';
  const isThreeDTransitionArrivalClear = inThreeDTransition && threeDExploreTransitionPhase === 'arrival-clear';
  const isThreeDTransitionVisualTail = isThreeDTransitionArrivalWhite || isThreeDTransitionArrivalClear;
  const hideTwoDOverviewForThreeDTransition = isThreeDTransitionFadeOut || isThreeDTransitionVisualTail;
  const threeDTransitionTwoDFilter = isThreeDTransitionWhiteBlur || isThreeDTransitionFadeOut
    ? 'blur(14px) saturate(0.12) brightness(1.7)'
    : 'none';
  const threeDTransitionTwoDTransform = isThreeDTransitionFadeOut ? 'scale(0.94)' : (isThreeDTransitionWhiteBlur ? 'scale(0.98)' : 'scale(1)');
  const threeDTransitionTwoDOpacity = hideTwoDOverviewForThreeDTransition ? 0 : 1;
  const threeDTransitionTerminalFilter = isThreeDTransitionWhiteBlur || isThreeDTransitionFadeOut
    ? 'blur(10px) saturate(0) brightness(2.1)'
    : 'none';
  const threeDTransitionTerminalOpacity = hideTwoDOverviewForThreeDTransition ? 0 : 1;
  const threeDTransitionTerminalTransform = isThreeDTransitionFadeOut ? 'scale(0.96)' : (isThreeDTransitionWhiteBlur ? 'scale(0.985)' : 'scale(1)');
  const showThreeDTransitionWhiteOverlay = inThreeDTransition && (isThreeDTransitionWhiteBlur || isThreeDTransitionFadeOut);
  const shouldHideThreeDSceneForTravel = inThreeDTransition && (
    isThreeDTransitionCode
    || isThreeDTransitionWhiteBlur
    || isThreeDTransitionFadeOut
  );
  const threeDArrivalAnimation = isThreeDTransitionArrivalWhite
    ? `dse-3d-arrive-white ${THREE_D_TRANSITION_ARRIVAL_WHITE_MS}ms cubic-bezier(0.18, 0.84, 0.3, 1) both`
    : (isThreeDTransitionArrivalClear
      ? `dse-3d-arrive-clear ${THREE_D_TRANSITION_ARRIVAL_CLEAR_MS}ms cubic-bezier(0.2, 0.85, 0.28, 1) both`
      : undefined);
  const normalizedPathname = String(location.pathname || '').trim().toLowerCase();
  const isGalleryOverviewRoute = normalizedPathname === '/gallery';
  const showMobileCategoryOverview = isMobileExperience && isGalleryOverviewRoute && !routeGallerySlug;
  const inTwoDOverview = isMobileExperience || overviewMode === '2d' || overviewMode === 'to3d';
  const inThreeDOverview = isThreeDTerminalExperienceEnabled && !isMobileExperience && (overviewMode === 'to3d' || overviewMode === '3d' || overviewMode === 'to2d');
  const useTwoDExploreRootLayout = inTwoDOverview && !hideTwoDOverviewForThreeDTransition;
  const showTwoDTerminalControls = isThreeDTerminalExperienceEnabled
    && !isMobileExperience
    && (overviewMode === '2d' || (overviewMode === 'to3d' && showTwoDTerminal));

  useEffect(() => {
    if (showMobileCategoryOverview) {
      return;
    }

    const focusedGallery = focusedTwoDGallery;

    if (!focusedGallery) {
      return;
    }

    const gallerySlug = slugifyRouteSegment(focusedGallery.title || focusedGallery.id || 'gallery', 'gallery');
    const galleryRouteId = String(focusedGallery.id || gallerySlug || 'gallery').trim();
    const mediaIndex = twoDMediaItems.length ? clampIndex(twoDMediaIndex, twoDMediaItems.length) : 0;
    const nextRoute = `/gallery/${encodeURIComponent(galleryRouteId)}/${mediaIndex}`;
    const currentRoute = `${location.pathname}${location.search || ''}`;
    if (currentRoute === nextRoute || lastSyncedRouteRef.current === nextRoute) {
      lastSyncedRouteRef.current = nextRoute;
      return;
    }

    lastSyncedRouteRef.current = nextRoute;
    navigate(nextRoute, { replace: true });
  }, [
    isMobileExperience,
    showMobileCategoryOverview,
    routeGallerySlug,
    focusedTwoDGallery,
    twoDMediaItems.length,
    twoDMediaIndex,
    twoDGalleryTransitionPhase,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!isThreeDTerminalExperienceEnabled || isMobileExperience) {
      clearTwoDTerminalAutoOpenTimer();
      setShowTwoDTerminal(false);
      return undefined;
    }

    if (overviewMode === '2d') {
      clearTwoDTerminalAutoOpenTimer();
      setShowTwoDTerminal(false);

      twoDTerminalAutoOpenTimerRef.current = window.setTimeout(() => {
        twoDTerminalAutoOpenTimerRef.current = null;
        setShowTwoDTerminal(true);
      }, TWO_D_TERMINAL_AUTO_OPEN_DELAY_MS);

      return () => {
        clearTwoDTerminalAutoOpenTimer();
      };
    }

    clearTwoDTerminalAutoOpenTimer();
    if (overviewMode === 'to3d') {
      return undefined;
    }

    setShowTwoDTerminal(false);
    return undefined;
  }, [overviewMode, clearTwoDTerminalAutoOpenTimer, isMobileExperience, isThreeDTerminalExperienceEnabled]);

  const sharedTerminalButtonWidth = `min(${TWO_D_TERMINAL_PANEL_WIDTH}px, calc(100vw - 48px))`;
  const sharedTerminalButtonTop = `calc(100vh - ${TWO_D_TERMINAL_BUTTON_BOTTOM + TWO_D_TERMINAL_BUTTON_HEIGHT}px)`;
  const sharedTerminalPanelTop = `calc(100vh - ${TWO_D_TERMINAL_PANEL_HEIGHT + TWO_D_TERMINAL_BUTTON_BOTTOM + TWO_D_TERMINAL_BUTTON_HEIGHT}px)`;

  const activeTwoDImageThumbSrc = activeTwoDMedia?.kind === 'image'
    ? ''
    : '';
  const activeTwoDImageWebSrc = activeTwoDMedia?.kind === 'image'
    ? String(activeTwoDMedia.optimizedSrc || '')
    : '';
  const activeTwoDImageHighSrc = activeTwoDMedia?.kind === 'image'
    ? String((twoDHiResEnabled && activeTwoDMedia.originalSrc) || '')
    : '';
  const twoDHasDistinctWebTier = !!activeTwoDImageWebSrc && activeTwoDImageWebSrc !== activeTwoDImageThumbSrc;
  const twoDHasDistinctHighTier = !!activeTwoDImageHighSrc && activeTwoDImageHighSrc !== activeTwoDImageWebSrc;
  const activeTwoDMediaDisplayIndex = twoDMediaItems.length
    ? clampIndex(twoDMediaIndex, twoDMediaItems.length) + 1
    : 0;
  const activeTwoDGalleryTitle = String(focusedTwoDGallery?.title || 'Gallery').trim() || 'Gallery';
  const activeTwoDAnimationToken = `${activeTwoDMediaId}:${twoDMediaAnimationKey}`;
  const twoDGalleryTransitioningToFocus = twoDGalleryTransitionPhase === 'focus-fade-others'
    || twoDGalleryTransitionPhase === 'focus-expand-picker'
    || twoDGalleryTransitionPhase === 'focus-collapse-overview';
  const twoDGalleryTransitioningToList = twoDGalleryTransitionPhase === 'list-collapse-picker'
    || twoDGalleryTransitionPhase === 'list-fade-in-others';
  const twoDFocusedSwitchCollapse = twoDFocusedSwitchPhase === 'collapse';
  const twoDFocusedSwitchWhitePulse = twoDFocusedSwitchPhase === 'white-pulse';
  const twoDFocusedSwitchTitlePulse = twoDFocusedSwitchPhase === 'title-pulse';
  const twoDFocusedSwitchRestore = twoDFocusedSwitchPhase === 'restore';
  const isTwoDFocusedSwitchBusy = twoDFocusedSwitchPhase !== 'idle';
  const isTwoDDesktopTransitioning = twoDGalleryTransitioningToFocus
    || twoDGalleryTransitioningToList
    || isTwoDFocusedSwitchBusy
    || twoDFocusEntryCollapsed;
  const keepFocusedShifterCollapsed = twoDFocusedSwitchCollapse
    || twoDFocusedSwitchWhitePulse
    || twoDFocusedSwitchTitlePulse
    || twoDFocusEntryCollapsed
    || twoDCollapseGate;
  const focusedSwitchWhiteOverlayAnimation = twoDFocusedSwitchWhitePulse
    ? `dse-2d-focused-switch-white-pulse ${TWO_D_FOCUSED_SWITCH_WHITE_PULSE_MS}ms cubic-bezier(0.22, 0.86, 0.36, 1) both`
    : (twoDFocusedSwitchRestore
      ? `dse-2d-focused-switch-white-fade-out ${TWO_D_FOCUSED_SWITCH_RESTORE_MS}ms ease both`
      : undefined);
  const toggleTwoDInfoPanel = () => {
    const nextOpen = !isTwoDInfoOpen;
    logTerminalCommandExecution(
      nextOpen ? 'KLIKK::VIS INFORMASJON' : 'KLIKK::SKJUL INFORMASJON',
      '2D.INFO.TOGGLE',
      `set info panel open=${nextOpen}`
    );
    setIsTwoDInfoOpen((prev) => !prev);
  };
  const showDesktopTwoDFocusedLayout = twoDGalleryTransitionPhase === 'focused'
    || twoDGalleryTransitionPhase === 'list-collapse-picker';
  const collapseOverviewToViewer = twoDGalleryTransitionPhase === 'focus-collapse-overview';
  const twoDPickerShellAnimation = twoDGalleryTransitionPhase === 'focus-expand-picker'
    ? `dse-2d-picker-height-expand ${TWO_D_GALLERY_STAGE_MS}ms cubic-bezier(0.2, 0.86, 0.24, 1) both`
    : (twoDGalleryTransitionPhase === 'list-collapse-picker'
      ? `dse-2d-picker-height-collapse ${TWO_D_GALLERY_STAGE_MS}ms cubic-bezier(0.42, 0, 0.68, 1) both`
      : undefined);
  const useFullTwoDFocusedViewport = useTwoDExploreRootLayout
    && (showDesktopTwoDFocusedLayout || (isMobileExperience && Boolean(focusedTwoDGallery)));

  const renderTwoDMediaPanel = (mobileLayout = false) => {
    const stackInfoBelowPreview = mobileLayout || isNarrowViewport;
    const wideSplitLayout = !stackInfoBelowPreview;
    const infoTransitionMs = 1000;
    const desktopInfoPanelWidthPx = 360;
    const showInfoColumn = isTwoDInfoOpen;
    const gridColumns = wideSplitLayout
      ? (isTwoDInfoOpen
        ? `minmax(0, 1fr) minmax(0, ${desktopInfoPanelWidthPx}px)`
        : 'minmax(0, 1fr) 0px')
      : '1fr';
    const previewHeight = '100%';
    const headerFontFamily = '"Space Grotesk", "Avenir Next", "Segoe UI", sans-serif';
    const bodyFontFamily = '"IBM Plex Mono", "Consolas", "Courier New", monospace';
    const kanvasViewportMode = activeTwoDMedia?.kind === 'box';
    const previewMediaPadding = kanvasViewportMode ? 0 : (mobileLayout ? 0 : 20);
    const previewMediaBorderRadius = kanvasViewportMode ? 0 : (mobileLayout ? 0 : 12);
    const zoomSupportedOnCurrentMedia = activeTwoDMedia?.kind === 'image';
    const zoomActiveOnCurrentMedia = twoDPressZoom.active
      && twoDPressZoom.mediaId === activeTwoDMediaId
      && zoomSupportedOnCurrentMedia;
    const zoomPositionKnownForCurrentMedia = twoDPressZoom.mediaId === activeTwoDMediaId
      && zoomSupportedOnCurrentMedia;
    const mediaZoomTransform = zoomActiveOnCurrentMedia ? `scale(${Math.max(1, twoDPressZoom.scale || 1)})` : 'scale(1)';
    const mediaZoomOrigin = zoomPositionKnownForCurrentMedia
      ? `${twoDPressZoom.x}% ${twoDPressZoom.y}%`
      : '50% 50%';
    const mediaObjectFit = 'contain';
    const outgoingImageLayerSource = twoDOutgoingMedia?.kind === 'image'
      ? String(twoDOutgoingMedia.optimizedSrc || '').trim()
      : '';
    const showOutgoingImageLayer = Boolean(outgoingImageLayerSource && twoDOutgoingMedia?.id);
    const slideDirection = twoDMediaSlideDirection < 0 ? -1 : 1;
    const outgoingMediaAnimationName = slideDirection > 0 ? 'dse-2d-media-exit-next' : 'dse-2d-media-exit-prev';
    const incomingMediaAnimationName = slideDirection > 0 ? 'dse-2d-media-enter-next' : 'dse-2d-media-enter-prev';
    const incomingMediaLayerKey = String(activeTwoDMediaId || activeTwoDAnimationToken || 'media');
    const mediaLoadingOverlayVisible = twoDMediaLoading || (!!activeTwoDMedia && !twoDMediaPreviewReady && !showOutgoingImageLayer);
    const infoBodyOpen = isTwoDInfoOpen;
    const labelFontSize = mobileLayout ? 15 : 17;
    const twoDHiResAvailable = activeTwoDMedia?.kind === 'image'
      && !!String(activeTwoDMedia?.originalSrc || '').trim()
      && String(activeTwoDMedia?.originalSrc || '').trim() !== String(activeTwoDMedia?.optimizedSrc || '').trim();
    const twoDHiResActive = twoDHiResAvailable && twoDHiResEnabled;
    const hasPeopleLists = activeTwoDParticipantEntries.length > 0 || activeTwoDCreatedByEntries.length > 0;
    const hasInfoContent = activeTwoDInfoRows.length > 0 || hasPeopleLists || activeTwoDLinkEntries.length > 0;

    const renderPeopleLists = (overlayMode = false) => {
      if (!hasPeopleLists) return null;

      const sections = [
        { title: 'Created by', rows: activeTwoDCreatedByEntries, keyPrefix: 'created-by' },
        { title: 'Participants', rows: activeTwoDParticipantEntries, keyPrefix: 'participants' },
      ].filter((section) => section.rows.length > 0);

      return (
        <div
          style={{
            width: '100%',
            display: 'grid',
            gap: 12,
            marginTop: overlayMode ? 2 : 6,
            textAlign: 'left',
          }}
        >
          {sections.map((section) => (
            <div key={section.keyPrefix} style={{ display: 'grid', gap: 6, alignContent: 'start', minWidth: 0 }}>
              <div
                style={{
                  color: 'rgba(207,222,255,0.9)',
                  fontSize: labelFontSize,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontFamily: bodyFontFamily,
                  textAlign: 'left',
                  lineHeight: 1.2,
                }}
              >
                {section.title}
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                {section.rows.map((line, index) => (
                  <SlotMachineText
                    key={`${section.keyPrefix}:${index}`}
                    text={String(line || '').trim()}
                    triggerKey={`${activeTwoDAnimationToken}:${section.keyPrefix}:${index}`}
                    durationScale={0.28}
                    staggerScale={0.35}
                    scrambleEvery={6}
                    style={{
                      color: '#f8fafc',
                      fontSize: mobileLayout ? 13 : 14,
                      lineHeight: 1.45,
                      fontFamily: bodyFontFamily,
                      wordBreak: 'break-word',
                      textAlign: 'left',
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    };

    const renderLinksSection = (overlayMode = false) => {
      if (!activeTwoDLinkEntries.length) return null;

      return (
        <div
          style={{
            width: '100%',
            display: 'grid',
            gap: 10,
            marginTop: overlayMode ? 4 : 12,
            textAlign: 'left',
          }}
        >
          <div
            style={{
              color: 'rgba(207,222,255,0.9)',
              fontSize: labelFontSize,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontFamily: bodyFontFamily,
              textAlign: 'left',
              lineHeight: 1.2,
            }}
          >
            Links
          </div>
          <div style={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
            {activeTwoDLinkEntries.map((link, index) => (
              <a
                key={`links:${link.id}:${index}`}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: mobileLayout ? 38 : 40,
                  width: 'fit-content',
                  justifySelf: 'start',
                  maxWidth: '100%',
                  padding: mobileLayout ? '9px 14px' : '10px 16px',
                  borderRadius: 999,
                  border: '1px solid rgba(160,208,255,0.56)',
                  background: 'linear-gradient(145deg, rgba(19,36,59,0.9), rgba(7,14,24,0.94))',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.28)',
                  color: '#d7ebff',
                  fontSize: mobileLayout ? 12 : 13,
                  fontFamily: bodyFontFamily,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  wordBreak: 'break-word',
                  textDecoration: 'none',
                  textAlign: 'left',
                }}
              >
                {link.text}
              </a>
            ))}
          </div>
        </div>
      );
    };

    const renderBaseInfoRows = (overlayMode = false) => activeTwoDInfoRows.map((row) => (
      <div
        key={`${overlayMode ? 'overlay' : 'stack'}-${row.label}`}
        style={{ display: 'grid', gap: 3, textAlign: 'left', justifyItems: 'start', width: '100%' }}
      >
        <div
          style={{
            color: 'rgba(207,222,255,0.84)',
            fontSize: labelFontSize,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontFamily: bodyFontFamily,
            textAlign: 'left',
            lineHeight: 1.2,
            width: '100%',
          }}
        >
          {row.label}
        </div>
        {Array.isArray(row.value) || (row.value && typeof row.value === 'object') ? (
          (() => {
            const structuredPairs = unpackStructuredInfoPairs(row.value);

            if (!structuredPairs.length) {
              return (
                <div
                  style={{
                    color: '#f8fafc',
                    fontSize: mobileLayout ? 13 : 14,
                    lineHeight: 1.45,
                    fontFamily: bodyFontFamily,
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  No specific information.
                </div>
              );
            }

            return (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: mobileLayout ? 'minmax(86px, 40%) 1fr' : 'minmax(112px, 34%) 1fr',
                  gap: mobileLayout ? '4px 10px' : '5px 12px',
                  alignItems: 'start',
                  width: '100%',
                }}
              >
                {structuredPairs.map((pair, index) => (
                  <React.Fragment key={`${row.label}:${pair.label}:${index}`}>
                    <div
                      style={{
                        color: 'rgba(207,222,255,0.84)',
                        fontSize: labelFontSize,
                        lineHeight: 1.35,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        fontFamily: bodyFontFamily,
                        wordBreak: 'break-word',
                        textAlign: 'left',
                      }}
                    >
                      {pair.label}
                    </div>
                    <SlotMachineText
                      text={pair.value}
                      triggerKey={`${activeTwoDAnimationToken}:${row.label}:pair:${index}`}
                      durationScale={0.28}
                      staggerScale={0.35}
                      scrambleEvery={6}
                      style={{
                        color: '#f8fafc',
                        fontSize: mobileLayout ? 13 : 14,
                        lineHeight: 1.45,
                        fontFamily: bodyFontFamily,
                        wordBreak: 'break-word',
                        textAlign: 'left',
                      }}
                    />
                  </React.Fragment>
                ))}
              </div>
            );
          })()
        ) : (
          <SlotMachineText
            text={String(row.value || '')}
            triggerKey={`${activeTwoDAnimationToken}:${row.label}`}
            durationScale={0.28}
            staggerScale={0.35}
            scrambleEvery={6}
            style={{
              color: '#f8fafc',
              fontSize: mobileLayout ? 13 : 14,
              lineHeight: 1.45,
              fontFamily: row.label === 'Description' ? headerFontFamily : bodyFontFamily,
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          />
        )}
      </div>
    ));

    return (
      <div
        style={{
          display: 'grid',
          gap: mobileLayout ? 10 : 14,
          gridTemplateColumns: gridColumns,
          transition: `grid-template-columns ${infoTransitionMs}ms ease`,
          minHeight: '100%',
          height: '100%',
          width: '100%',
          justifySelf: 'center',
          minWidth: 0,
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateRows: '1fr',
            gap: 10,
            minHeight: 0,
            height: '100%',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              display: 'none',
              alignItems: 'center',
              gap: 10,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.28)',
              background: 'rgba(6,11,21,0.74)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              padding: mobileLayout ? '8px 10px' : '9px 12px',
              overflow: 'hidden',
            }}
          >
            <label
              style={{
                display: 'grid',
                gap: 4,
                flex: 1,
                minWidth: 0,
                color: '#f8fafc',
                fontFamily: bodyFontFamily,
              }}
              title={`Offset for click-and-hold zoom. Current hold zoom ${twoDHoldZoomScale.toFixed(1)}x`}
            >
              <span style={{ fontSize: mobileLayout ? 10 : 11, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.86 }}>Hold zoom</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                <input
                  type="range"
                  min={TWO_D_HOLD_ZOOM_OFFSET_MIN}
                  max={twoDHoldZoomOffsetMax}
                  step={TWO_D_HOLD_ZOOM_OFFSET_STEP}
                  value={twoDHoldZoomOffset}
                  onChange={(event) => setTwoDHoldZoomOffset(clampTwoDHoldZoomOffset(Number(event.target.value), twoDHoldZoomOffsetMax))}
                  aria-label="Click and hold zoom offset"
                  style={{ width: '100%' }}
                />
                <span style={{ minWidth: 46, textAlign: 'right', fontSize: mobileLayout ? 11 : 12, letterSpacing: '0.08em' }}>
                  {twoDHoldZoomOffset >= 0 ? '+' : ''}{twoDHoldZoomOffset.toFixed(1)}
                </span>
              </div>
            </label>

            <button
              type="button"
              onClick={() => {
                if (!twoDHiResAvailable) return;
                setTwoDHiResEnabled((prev) => !prev);
              }}
              disabled={!twoDHiResAvailable}
              style={{
                borderRadius: 9,
                border: `1px solid ${twoDHiResActive ? 'rgba(255,232,163,0.92)' : 'rgba(255,255,255,0.35)'}`,
                background: twoDHiResActive ? 'rgba(76,58,16,0.92)' : 'rgba(10,16,28,0.88)',
                color: twoDHiResActive ? '#fff4cf' : '#eef2ff',
                padding: mobileLayout ? '9px 12px' : '10px 14px',
                fontSize: mobileLayout ? 11 : 12,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: twoDHiResAvailable ? 'pointer' : 'default',
                opacity: twoDHiResAvailable ? 1 : 0.55,
                whiteSpace: 'nowrap',
              }}
            >
              HI RES
            </button>
          </div>

          <div
            style={{
              position: 'relative',
              height: previewHeight,
              minHeight: 0,
              borderRadius: mobileLayout ? 14 : 18,
              border: '1px solid rgba(255,255,255,0.24)',
              background: 'rgba(0,0,0,0.6)',
              overflow: 'hidden',
              boxShadow: '0 24px 48px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.15)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 20,
                left: 20,
                right: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 8,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (isTwoDDesktopTransitioning) return;
                  logTerminalCommandExecution('KLIKK::VIS ALLE GALLERIER', '2D.GALLERY.VIS_ALLE', 'reverse focus animation to list');
                  startTwoDBackToListTransition();
                }}
                aria-label="Show gallery list"
                title="Show gallery list"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.45)',
                  background: 'rgba(8,14,25,0.82)',
                  color: '#f8fafc',
                  fontSize: 38,
                  lineHeight: 1,
                  fontWeight: 700,
                  cursor: 'pointer',
                  opacity: isTwoDDesktopTransitioning ? 0.56 : 1,
                }}
              >
                ≡
              </button>

              <button
                type="button"
                onClick={toggleTwoDInfoPanel}
                aria-label="Show information"
                title="Show information"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.45)',
                  background: isTwoDInfoOpen ? 'rgba(26,50,92,0.9)' : 'rgba(8,14,25,0.82)',
                  color: '#f8fafc',
                  fontSize: 34,
                  lineHeight: 1,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                I
              </button>
            </div>

            <div
              style={{
                position: 'absolute',
                top: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 8,
                textAlign: 'center',
                color: '#f9fafb',
                fontFamily: bodyFontFamily,
                letterSpacing: '0.12em',
                fontSize: mobileLayout ? 11 : 12,
                textTransform: 'uppercase',
                maxWidth: '62%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                background: 'rgba(8,14,25,0.7)',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.24)',
                padding: '6px 12px',
              }}
            >
              {twoDMediaItems.length
                ? `${activeTwoDGalleryTitle.toUpperCase()} ${activeTwoDMediaDisplayIndex} / ${twoDMediaItems.length}`
                : `${activeTwoDGalleryTitle.toUpperCase()} 0 / 0`}
            </div>

            <button
              type="button"
              onClick={() => {
                logTerminalCommandExecution('KLIKK::FORRIGE MEDIA', '2D.MEDIA.PREV', 'move media cursor -1');
                moveTwoDMedia(-1);
              }}
              disabled={twoDMediaItems.length <= 1}
              aria-label="Previous media"
              title="Previous media"
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                width: mobileLayout ? 40 : 46,
                height: mobileLayout ? 40 : 46,
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.46)',
                background: 'rgba(8,14,25,0.82)',
                color: '#fff',
                fontSize: mobileLayout ? 26 : 30,
                lineHeight: 1,
                cursor: twoDMediaItems.length <= 1 ? 'default' : 'pointer',
                opacity: twoDMediaItems.length <= 1 ? 0.4 : 1,
                zIndex: 8,
              }}
            >
              ❮
            </button>

            <button
              type="button"
              onClick={() => {
                logTerminalCommandExecution('KLIKK::NESTE MEDIA', '2D.MEDIA.NEXT', 'move media cursor +1');
                moveTwoDMedia(1);
              }}
              disabled={twoDMediaItems.length <= 1}
              aria-label="Next media"
              title="Next media"
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                width: mobileLayout ? 40 : 46,
                height: mobileLayout ? 40 : 46,
                borderRadius: '999px',
                border: '1px solid rgba(255,255,255,0.46)',
                background: 'rgba(8,14,25,0.82)',
                color: '#fff',
                fontSize: mobileLayout ? 26 : 30,
                lineHeight: 1,
                cursor: twoDMediaItems.length <= 1 ? 'default' : 'pointer',
                opacity: twoDMediaItems.length <= 1 ? 0.4 : 1,
                zIndex: 8,
              }}
            >
              ❯
            </button>

            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 4,
                pointerEvents: 'none',
                opacity: mediaLoadingOverlayVisible ? 1 : 0,
                transition: 'opacity 460ms ease',
                background: 'linear-gradient(130deg, rgba(4,10,19,0.86), rgba(9,17,30,0.72) 46%, rgba(4,10,19,0.86))',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: '-30% -45%',
                  background: 'linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,0.22), rgba(255,255,255,0))',
                  animation: 'dse-2d-media-loading-shimmer 1300ms linear infinite',
                }}
              />
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.25)', borderTopColor: '#ffffff', animation: 'dse-spin 0.9s linear infinite' }} />
              </div>
            </div>

            {showOutgoingImageLayer ? (
              <div
                key={`two-d-media-out-${String(twoDOutgoingMedia?.id || 'media')}`}
                style={{
                  position: 'absolute',
                  inset: previewMediaPadding,
                  borderRadius: previewMediaBorderRadius,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                  zIndex: 1,
                  animation: `${outgoingMediaAnimationName} ${TWO_D_MEDIA_TRANSITION_MS}ms cubic-bezier(0.2, 0.85, 0.3, 1) both`,
                }}
              >
                <img
                  src={outgoingImageLayerSource}
                  alt=""
                  aria-hidden
                  draggable={false}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: mediaObjectFit,
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ) : null}

            {activeTwoDMedia ? (
              <div
                key={`two-d-media-in-${incomingMediaLayerKey}`}
                style={{
                  position: 'absolute',
                  inset: previewMediaPadding,
                  borderRadius: previewMediaBorderRadius,
                  overflow: 'hidden',
                  background: 'transparent',
                  animation: `${incomingMediaAnimationName} ${TWO_D_MEDIA_TRANSITION_MS}ms cubic-bezier(0.2, 0.85, 0.3, 1) both`,
                  zIndex: 2,
                }}
              >
                {activeTwoDMedia.kind === 'image' ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      cursor: zoomActiveOnCurrentMedia ? 'zoom-out' : 'zoom-in',
                      touchAction: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                    }}
                    {...getTwoDPressZoomHandlers(activeTwoDMediaId)}
                  >
                    {activeTwoDImageThumbSrc ? (
                      <img
                        src={activeTwoDImageThumbSrc}
                        alt={activeTwoDMedia.title || 'Galleri-bilde'}
                        draggable={false}
                        onLoad={(event) => {
                          updateTwoDImageAspectRatioFromElement(event.currentTarget);
                          setTwoDThumbResLoaded(true);
                          if (!twoDHasDistinctWebTier) {
                            setTwoDWebResLoaded(true);
                          }
                          setTwoDMediaPreviewReady(true);
                        }}
                        onError={() => {
                          setTwoDThumbResLoaded(true);
                          if (!twoDHasDistinctWebTier) {
                            setTwoDWebResLoaded(true);
                          }
                          setTwoDMediaPreviewReady(true);
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: mediaObjectFit,
                          filter: 'saturate(0.9) contrast(1.02)',
                          opacity: twoDThumbResLoaded
                            ? (twoDHasDistinctWebTier
                              ? (twoDWebResLoaded ? 0 : 1)
                              : (twoDHasDistinctHighTier
                                ? (twoDHighResLoaded ? 0 : 1)
                                : 1))
                            : 0,
                          transform: mediaZoomTransform,
                          transformOrigin: mediaZoomOrigin,
                          transition: `opacity 380ms ease, transform ${TWO_D_PRESS_ZOOM_TRANSITION_MS}ms ease`,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null}

                    {activeTwoDImageWebSrc && !twoDWebResFailed && (twoDHasDistinctWebTier || !activeTwoDImageThumbSrc) ? (
                      <img
                        src={activeTwoDImageWebSrc}
                        alt={activeTwoDMedia.title || 'Galleri-bilde'}
                        draggable={false}
                        onLoad={(event) => {
                          updateTwoDImageAspectRatioFromElement(event.currentTarget);
                          setTwoDWebResLoaded(true);
                          setTwoDMediaPreviewReady(true);
                        }}
                        onError={() => {
                          setTwoDWebResFailed(true);
                          setTwoDWebResLoaded(false);
                          setTwoDMediaPreviewReady(true);
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: mediaObjectFit,
                          opacity: twoDWebResLoaded ? (twoDHasDistinctHighTier && twoDHighResLoaded && !twoDHighResFailed ? 0 : 1) : 0,
                          transform: mediaZoomTransform,
                          transformOrigin: mediaZoomOrigin,
                          transition: `opacity 420ms ease, transform ${TWO_D_PRESS_ZOOM_TRANSITION_MS}ms ease`,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null}

                    {activeTwoDImageHighSrc && twoDHasDistinctHighTier && twoDAllowHighResLoad && twoDMediaPreviewReady && twoDWebResLoaded && !twoDHighResFailed ? (
                      <img
                        src={activeTwoDImageHighSrc}
                        alt={activeTwoDMedia.title || 'Galleri-bilde'}
                        draggable={false}
                        onLoad={(event) => {
                          updateTwoDImageAspectRatioFromElement(event.currentTarget);
                          setTwoDHighResLoaded(true);
                          setTwoDHighResFailed(false);
                          setTwoDMediaPreviewReady(true);
                        }}
                        onError={() => {
                          setTwoDHighResFailed(true);
                          setTwoDHighResLoaded(false);
                          setTwoDMediaPreviewReady(true);
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: mediaObjectFit,
                          opacity: twoDHighResLoaded ? 1 : 0,
                          transform: mediaZoomTransform,
                          transformOrigin: mediaZoomOrigin,
                          transition: `opacity 520ms ease, transform ${TWO_D_PRESS_ZOOM_TRANSITION_MS}ms ease`,
                          pointerEvents: 'none',
                        }}
                      />
                    ) : null}

                  </div>
                ) : activeTwoDMedia.kind === 'box' ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      cursor: 'pointer',
                      touchAction: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                    }}
                    {...getTwoDPressZoomHandlers(activeTwoDMediaId, { allowPressZoom: false })}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        transform: mediaZoomTransform,
                        transformOrigin: mediaZoomOrigin,
                        transition: `transform ${TWO_D_PRESS_ZOOM_TRANSITION_MS}ms ease`,
                      }}
                    >
                      <PhotoGallery3D
                        key={`embedded-kanvas-${String(focusedTwoDGallery?.id || focusedTwoDGallery?.title || 'gallery')}-${String(activeTwoDMedia?.id || 'item')}`}
                        mode="discover"
                        embedded
                        embeddedGalleryId={String(focusedTwoDGallery?.id || focusedTwoDGallery?.galleryId || focusedTwoDGallery?.title || '')}
                        embeddedItemId={String(activeTwoDMedia?.id || '')}
                        onEmbeddedReady={() => setTwoDMediaPreviewReady(true)}
                      />
                    </div>
                  </div>
                ) : activeTwoDMedia.youtubeId ? (
                  <iframe
                    title={`youtube-${activeTwoDMedia.id}`}
                    src={`https://www.youtube.com/embed/${encodeURIComponent(activeTwoDMedia.youtubeId)}?rel=0&modestbranding=1&autoplay=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    onLoad={() => setTwoDMediaPreviewReady(true)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent',
                    }}
                  />
                ) : activeTwoDMedia.videoSrc ? (
                  <video
                    key={activeTwoDMedia.id}
                    controls
                    preload="metadata"
                    poster={activeTwoDMedia.posterSrc || undefined}
                    onLoadedData={() => setTwoDMediaPreviewReady(true)}
                    onError={() => setTwoDMediaPreviewReady(true)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: mediaObjectFit,
                      background: 'transparent',
                    }}
                  >
                    <source src={activeTwoDMedia.videoSrc} />
                  </video>
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#f3f3f3' }}>
                    Ingen media tilgjengelig.
                  </div>
                )}
              </div>
            ) : null}

            {!twoDMediaLoading && !activeTwoDMedia ? (
              <div style={{ position: 'absolute', inset: previewMediaPadding, borderRadius: 12, display: 'grid', placeItems: 'center', color: '#f3f3f3', padding: 20, textAlign: 'center', background: 'transparent' }}>
                Ingen bilder eller videoer i dette galleriet enn aa.
              </div>
            ) : null}

          </div>

            {twoDMediaError && !showInfoColumn ? (
              <div style={{ borderRadius: 10, border: '1px solid rgba(239,68,68,0.45)', background: 'rgba(88,12,12,0.48)', color: '#ffd8d8', padding: '10px 12px', fontSize: 12 }}>
                {twoDMediaError}
              </div>
            ) : null}
        </div>

        {(wideSplitLayout || stackInfoBelowPreview) ? (
          <div
            style={{
              display: 'grid',
              gap: 10,
              alignContent: 'start',
              minHeight: 0,
              overflowY: stackInfoBelowPreview ? 'visible' : 'auto',
              opacity: wideSplitLayout && !isTwoDInfoOpen ? 0 : 1,
              transform: wideSplitLayout && !isTwoDInfoOpen ? 'translateX(16px)' : 'translateX(0px)',
              pointerEvents: wideSplitLayout && !isTwoDInfoOpen ? 'none' : 'auto',
              transition: `opacity ${infoTransitionMs}ms ease, transform ${infoTransitionMs}ms ease`,
              overflow: 'hidden',
              textAlign: 'left',
              minWidth: 0,
            }}
          >
            {twoDMediaError ? (
              <div style={{ borderRadius: 10, border: '1px solid rgba(239,68,68,0.45)', background: 'rgba(88,12,12,0.48)', color: '#ffd8d8', padding: '10px 12px', fontSize: 12 }}>
                {twoDMediaError}
              </div>
            ) : null}

            <div
              style={{
                maxHeight: infoBodyOpen ? 2200 : 0,
                opacity: infoBodyOpen ? 1 : 0,
                transform: infoBodyOpen ? 'translateY(0px)' : 'translateY(-8px)',
                overflow: 'hidden',
                transition: `max-height ${infoTransitionMs}ms ease, opacity ${infoTransitionMs}ms ease, transform ${infoTransitionMs}ms ease`,
              }}
            >
              {hasInfoContent ? (
                <div
                  key={`two-d-info-${activeTwoDAnimationToken}`}
                  style={{
                    borderRadius: mobileLayout ? 12 : 14,
                    border: '1px solid rgba(255,255,255,0.22)',
                    background: 'linear-gradient(165deg, rgba(4,7,14,0.92), rgba(8,14,24,0.86))',
                    padding: mobileLayout ? '11px 12px' : '14px 16px',
                    display: 'grid',
                    gap: mobileLayout ? 8 : 10,
                    animation: 'dse-2d-info-enter 360ms cubic-bezier(0.22, 1, 0.36, 1) both',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.28)',
                      background: 'rgba(6,11,21,0.74)',
                      backdropFilter: 'blur(5px)',
                      WebkitBackdropFilter: 'blur(5px)',
                      padding: mobileLayout ? '8px 10px' : '9px 12px',
                      overflow: 'hidden',
                    }}
                  >
                    <label
                      style={{
                        display: 'grid',
                        gap: 4,
                        flex: 1,
                        minWidth: 0,
                        color: '#f8fafc',
                        fontFamily: bodyFontFamily,
                      }}
                      title={`Offset for click-and-hold zoom. Current hold zoom ${twoDHoldZoomScale.toFixed(1)}x`}
                    >
                      <span style={{ fontSize: mobileLayout ? 10 : 11, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.86 }}>Hold zoom</span>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                        <input
                          type="range"
                          min={TWO_D_HOLD_ZOOM_OFFSET_MIN}
                          max={twoDHoldZoomOffsetMax}
                          step={TWO_D_HOLD_ZOOM_OFFSET_STEP}
                          value={twoDHoldZoomOffset}
                          onChange={(event) => setTwoDHoldZoomOffset(clampTwoDHoldZoomOffset(Number(event.target.value), twoDHoldZoomOffsetMax))}
                          aria-label="Click and hold zoom offset"
                          style={{ width: '100%' }}
                        />
                        <span style={{ minWidth: 46, textAlign: 'right', fontSize: mobileLayout ? 11 : 12, letterSpacing: '0.08em' }}>
                          {twoDHoldZoomOffset >= 0 ? '+' : ''}{twoDHoldZoomOffset.toFixed(1)}
                        </span>
                      </div>
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        if (!twoDHiResAvailable) return;
                        setTwoDHiResEnabled((prev) => !prev);
                      }}
                      disabled={!twoDHiResAvailable}
                      style={{
                        borderRadius: 9,
                        border: `1px solid ${twoDHiResActive ? 'rgba(255,232,163,0.92)' : 'rgba(255,255,255,0.35)'}`,
                        background: twoDHiResActive ? 'rgba(76,58,16,0.92)' : 'rgba(10,16,28,0.88)',
                        color: twoDHiResActive ? '#fff4cf' : '#eef2ff',
                        padding: mobileLayout ? '9px 12px' : '10px 14px',
                        fontSize: mobileLayout ? 11 : 12,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        cursor: twoDHiResAvailable ? 'pointer' : 'default',
                        opacity: twoDHiResAvailable ? 1 : 0.55,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      HI RES
                    </button>
                  </div>

                  {renderBaseInfoRows(false)}
                  {renderPeopleLists(false)}
                  {renderLinksSection(false)}
                </div>
              ) : (
                <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(6,10,18,0.64)', color: '#dbeafe', padding: '12px 14px', fontSize: 12, lineHeight: 1.45, display: 'grid', gap: 10 }}>
                  <div
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.28)',
                      background: 'rgba(6,11,21,0.74)',
                      backdropFilter: 'blur(5px)',
                      WebkitBackdropFilter: 'blur(5px)',
                      padding: mobileLayout ? '8px 10px' : '9px 12px',
                      overflow: 'hidden',
                    }}
                  >
                    <label
                      style={{
                        display: 'grid',
                        gap: 4,
                        flex: 1,
                        minWidth: 0,
                        color: '#f8fafc',
                        fontFamily: bodyFontFamily,
                      }}
                      title={`Offset for click-and-hold zoom. Current hold zoom ${twoDHoldZoomScale.toFixed(1)}x`}
                    >
                      <span style={{ fontSize: mobileLayout ? 10 : 11, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.86 }}>Hold zoom</span>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                        <input
                          type="range"
                          min={TWO_D_HOLD_ZOOM_OFFSET_MIN}
                          max={twoDHoldZoomOffsetMax}
                          step={TWO_D_HOLD_ZOOM_OFFSET_STEP}
                          value={twoDHoldZoomOffset}
                          onChange={(event) => setTwoDHoldZoomOffset(clampTwoDHoldZoomOffset(Number(event.target.value), twoDHoldZoomOffsetMax))}
                          aria-label="Click and hold zoom offset"
                          style={{ width: '100%' }}
                        />
                        <span style={{ minWidth: 46, textAlign: 'right', fontSize: mobileLayout ? 11 : 12, letterSpacing: '0.08em' }}>
                          {twoDHoldZoomOffset >= 0 ? '+' : ''}{twoDHoldZoomOffset.toFixed(1)}
                        </span>
                      </div>
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        if (!twoDHiResAvailable) return;
                        setTwoDHiResEnabled((prev) => !prev);
                      }}
                      disabled={!twoDHiResAvailable}
                      style={{
                        borderRadius: 9,
                        border: `1px solid ${twoDHiResActive ? 'rgba(255,232,163,0.92)' : 'rgba(255,255,255,0.35)'}`,
                        background: twoDHiResActive ? 'rgba(76,58,16,0.92)' : 'rgba(10,16,28,0.88)',
                        color: twoDHiResActive ? '#fff4cf' : '#eef2ff',
                        padding: mobileLayout ? '9px 12px' : '10px 14px',
                        fontSize: mobileLayout ? 11 : 12,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        cursor: twoDHiResAvailable ? 'pointer' : 'default',
                        opacity: twoDHiResAvailable ? 1 : 0.55,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      HI RES
                    </button>
                  </div>

                  <div>Informasjon vises her naar media har metadata.</div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  if (error) {
    return <div style={{ padding: 24, color: '#fff', background: '#000', minHeight: '100vh' }}>Error: {error}</div>;
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#000', overflow: 'hidden' }}>
      <style>{`
        @keyframes dse-terminal-pulse {
          0% { box-shadow: 0 0 0 0 rgba(220,38,38,0), 0 0 12px rgba(127,29,29,0.28); }
          50% { box-shadow: 0 0 0 16px rgba(220,38,38,0), 0 0 40px rgba(220,38,38,0.75); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0), 0 0 12px rgba(127,29,29,0.28); }
        }
        @keyframes dse-matrix-enter-opacity {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes dse-matrix-rain {
          0% {
            transform: translateX(-50%) translateY(-115%);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translateX(-50%) translateY(170%);
            opacity: 0;
          }
        }
        @keyframes dse-matrix-flicker {
          0%, 2%, 4%, 7%, 100% {
            filter: brightness(1) saturate(1);
          }
          1% {
            filter: brightness(1.05) saturate(1.08);
          }
          3% {
            filter: brightness(0.96) saturate(0.96);
          }
          6% {
            filter: brightness(1.08) saturate(1.1);
          }
        }
        @keyframes dse-matrix-scanline {
          0% {
            transform: translateY(-6px);
          }
          100% {
            transform: translateY(6px);
          }
        }
        @keyframes dse-terminal-pulse-text {
          0% { color: #ffffff; text-shadow: 0 0 8px rgba(255,255,255,0.25); }
          50% { color: #ff4d4d; text-shadow: 0 0 18px rgba(239,68,68,0.72); }
          100% { color: #ffffff; text-shadow: 0 0 8px rgba(255,255,255,0.25); }
        }
        @keyframes dse-blink {
          0% { opacity: 1; }
          50% { opacity: 0.2; }
          100% { opacity: 1; }
        }
        @keyframes dse-2d-media-enter-next {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(0%);
          }
        }
        @keyframes dse-2d-media-enter-prev {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(0%);
          }
        }
        @keyframes dse-2d-media-exit-next {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        @keyframes dse-2d-media-exit-prev {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        @keyframes dse-2d-media-loading-shimmer {
          0% {
            transform: translateX(-40%) rotate(8deg);
          }
          100% {
            transform: translateX(38%) rotate(8deg);
          }
        }
        @keyframes dse-2d-info-enter {
          0% {
            opacity: 0.45;
            transform: translateY(6px);
          }
          100% {
            opacity: 1;
            transform: translateY(0px);
          }
        }
        @keyframes dse-2d-info-popup-unfold {
          0% {
            -webkit-clip-path: inset(0 calc(100% - 0px) calc(100% - 20px) 0);
            clip-path: inset(0 calc(100% - 0px) calc(100% - 20px) 0);
            opacity: 0.14;
          }
          33.333% {
            -webkit-clip-path: inset(0 calc(100% - 20px) calc(100% - 20px) 0);
            clip-path: inset(0 calc(100% - 20px) calc(100% - 20px) 0);
            opacity: 0.55;
          }
          66.666% {
            -webkit-clip-path: inset(0 calc(100% - 20px) 0 0);
            clip-path: inset(0 calc(100% - 20px) 0 0);
            opacity: 0.82;
          }
          100% {
            -webkit-clip-path: inset(0 0 0 0);
            clip-path: inset(0 0 0 0);
            opacity: 1;
          }
        }
        @keyframes dse-2d-info-popup-content-in {
          0% {
            opacity: 0;
            transform: translateY(8px);
            filter: blur(3px);
          }
          100% {
            opacity: 1;
            transform: translateY(0px);
            filter: blur(0px);
          }
        }
        @keyframes dse-2d-card-center-in {
          0% {
            opacity: 0.82;
            transform: translate(-50%, calc(-50% + 28px)) scale(0.92);
            filter: blur(7px);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
            filter: blur(0px);
          }
        }
        @keyframes dse-2d-picker-height-expand {
          0% {
            max-height: 0;
            opacity: 0;
            transform: translateY(22px);
          }
          100% {
            max-height: 2200px;
            opacity: 1;
            transform: translateY(0px);
          }
        }
        @keyframes dse-2d-picker-height-collapse {
          0% {
            max-height: 2200px;
            opacity: 1;
            transform: translateY(0px);
          }
          100% {
            max-height: 0;
            opacity: 0;
            transform: translateY(18px);
          }
        }
        @keyframes dse-2d-focused-switch-white-pulse {
          0% {
            opacity: 0.14;
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
          }
          50% {
            opacity: 0.92;
            backdrop-filter: blur(18px);
            -webkit-backdrop-filter: blur(18px);
          }
          100% {
            opacity: 0.26;
            backdrop-filter: blur(6px);
            -webkit-backdrop-filter: blur(6px);
          }
        }
        @keyframes dse-2d-focused-switch-white-fade-out {
          0% {
            opacity: 0.24;
            backdrop-filter: blur(7px);
            -webkit-backdrop-filter: blur(7px);
          }
          100% {
            opacity: 0;
            backdrop-filter: blur(0px);
            -webkit-backdrop-filter: blur(0px);
          }
        }
        @keyframes dse-2d-focused-title-glow {
          0% {
            border-color: rgba(255,255,255,0.24);
            box-shadow: 0 10px 24px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08);
            filter: blur(0px);
          }
          50% {
            border-color: rgba(255,255,255,0.98);
            box-shadow: 0 0 34px rgba(255,255,255,0.95), 0 0 80px rgba(255,255,255,0.8), 0 10px 24px rgba(0,0,0,0.34);
            filter: blur(1.6px);
          }
          100% {
            border-color: rgba(255,255,255,0.3);
            box-shadow: 0 14px 34px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.12);
            filter: blur(0px);
          }
        }
        @keyframes dse-3d-arrive-white {
          0% {
            opacity: 0;
            filter: blur(20px) brightness(2.3) saturate(0);
          }
          62% {
            opacity: 1;
            filter: blur(14px) brightness(2.05) saturate(0.08);
          }
          100% {
            opacity: 1;
            filter: blur(10px) brightness(1.78) saturate(0.2);
          }
        }
        @keyframes dse-3d-arrive-clear {
          0% {
            opacity: 1;
            filter: blur(10px) brightness(1.78) saturate(0.2);
          }
          100% {
            opacity: 1;
            filter: blur(0px) brightness(1) saturate(1);
          }
        }
        @keyframes dse-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {requireRotation && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 34,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            color: '#fff',
            background: 'rgba(0, 0, 0, 0.94)',
          }}
        >
          <div style={{ display: 'grid', gap: 12, maxWidth: 420 }}>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Rotate your phone</div>
            <div style={{ fontSize: 18, lineHeight: 1.5, opacity: 0.85 }}>Switch to landscape mode to view the Discover galleries.</div>
          </div>
        </div>
      )}

      {inTwoDOverview ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 1,
            opacity: threeDTransitionTwoDOpacity,
            transform: threeDTransitionTwoDTransform,
            filter: threeDTransitionTwoDFilter,
            transition: `opacity ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1), transform ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1), filter ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1)`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 50% 82%, rgba(43,78,124,0.16), rgba(5,10,20,0) 62%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 50% 100%, rgba(102,161,214,0.16), rgba(10,15,30,0) 56%)',
            }}
          />
        </div>
      ) : null}

      <div className="explore-root" style={{ position: 'absolute', top: useTwoDExploreRootLayout ? (isMobileExperience ? MOBILE_EXPLORE_TOP_OFFSET_PX : 'var(--app-header-height, 78px)') : (useFullTwoDFocusedViewport ? 0 : 75), left: 0, right: 0, bottom: 0, overflow: 'hidden', display: 'flex', alignItems: useTwoDExploreRootLayout ? 'flex-start' : 'center', justifyContent: 'center', zIndex: 2 }}>
        <div className="flag-bg" aria-hidden>
          <img src="/images/Fontego_Hvit.png" alt="DODSEGO" />
        </div>

        {loading ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', animation: 'dse-spin 0.9s linear infinite' }} />
            <style>{`@keyframes dse-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : null}

        <div
          style={{
            width: useFullTwoDFocusedViewport
              ? '100vw'
              : (isMobileExperience ? 'min(1340px, 100vw)' : 'min(1340px, 94vw)'),
            maxWidth: useFullTwoDFocusedViewport ? '100vw' : undefined,
            height: useFullTwoDFocusedViewport
              ? '100%'
              : (inTwoDOverview
              ? (isMobileExperience ? 'calc(100% - 8px)' : `calc(100% - ${TWO_D_TERMINAL_BUTTON_BOTTOM}px)`)
              : '40vh'),
            minHeight: useFullTwoDFocusedViewport
              ? '100%'
              : (inTwoDOverview
              ? (isMobileExperience ? 'calc(100% - 8px)' : `calc(100% - ${TWO_D_TERMINAL_BUTTON_BOTTOM}px)`)
              : '40vh'),
            maxHeight: useFullTwoDFocusedViewport
              ? '100%'
              : (inTwoDOverview
              ? (isMobileExperience ? 'calc(100% - 8px)' : `calc(100% - ${TWO_D_TERMINAL_BUTTON_BOTTOM}px)`)
              : '40vh'),
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            zIndex: 2,
            opacity: inTwoDOverview ? threeDTransitionTwoDOpacity : 0,
            transform: threeDTransitionTwoDTransform,
            filter: threeDTransitionTwoDFilter,
            transition: `opacity ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1), transform ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1), filter ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1)`,
            pointerEvents: !requireRotation && inTwoDOverview ? 'auto' : 'none',
          }}
        >
          {isMobileExperience ? (
            showMobileCategoryOverview ? (
              <GalleryCategoryOverview
                categories={galleries}
                previewByCategory={galleryCardPreviews}
                getCategoryDescription={resolveGalleryCardDescription}
                emptyMessage="Ingen gallerier tilgjengelig akkurat naa."
              />
            ) : (
              <div style={{ display: 'grid', gap: 12, alignContent: 'start', padding: '10px 0 12px' }}>
                {galleries.length ? (
                  <>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <label style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(219,234,254,0.9)', fontFamily: '"IBM Plex Mono", "Consolas", "Courier New", monospace' }}>
                        CHOOSE GALLERY
                      </label>
                      <select
                        value={twoDFocusedGalleryId || galleries[0].id}
                        onChange={(event) => {
                          const nextGalleryId = String(event.target.value || '').trim();
                          if (!nextGalleryId) return;
                          logTerminalCommandExecution(`KLIKK::VELG GALLERI ${nextGalleryId}`, '2D.GALLERY.SELECT_MOBILE', `load gallery ${nextGalleryId}`);
                          loadTwoDGalleryMedia(nextGalleryId);
                        }}
                        style={{
                          borderRadius: 11,
                          border: '1px solid rgba(255,255,255,0.3)',
                          background: 'linear-gradient(165deg, rgba(6,10,18,0.96), rgba(3,6,12,0.98))',
                          color: '#fff',
                          padding: '11px 12px',
                          fontSize: 14,
                          fontFamily: '"Space Grotesk", "Avenir Next", "Segoe UI", sans-serif',
                        }}
                      >
                        {galleries.map((gallery) => (
                          <option key={`mobile-${gallery.id}`} value={gallery.id}>{gallery.title}</option>
                        ))}
                      </select>
                    </div>

                    {renderTwoDMediaPanel(true)}
                  </>
                ) : (
                  <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.45)', color: '#fff', textAlign: 'center', padding: 14 }}>
                    Ingen gallerier tilgjengelig akkurat naa.
                  </div>
                )}
              </div>
            )
          ) : (
            <div style={{ position: 'relative', minHeight: '100%', height: '100%' }}>
              {showDesktopTwoDFocusedLayout && focusedTwoDGallery ? (
                <div style={{ display: 'grid', gridTemplateRows: '1fr', gap: 0, alignContent: 'stretch', minHeight: '100%', height: '100%' }}>

                  <div
                    style={{
                      position: 'relative',
                      minHeight: 0,
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      paddingBottom: 4,
                      animation: twoDPickerShellAnimation,
                      maxHeight: twoDGalleryTransitionPhase === 'list-collapse-picker'
                        ? 0
                        : (keepFocusedShifterCollapsed ? 20 : 2400),
                      opacity: twoDGalleryTransitionPhase === 'list-collapse-picker' ? 0 : 1,
                      transform: twoDGalleryTransitionPhase === 'list-collapse-picker' ? 'translateY(18px)' : 'translateY(0px)',
                      transition: `max-height ${TWO_D_FOCUSED_SWITCH_COLLAPSE_MS}ms ease, opacity ${TWO_D_GALLERY_STAGE_MS}ms ease, transform ${TWO_D_GALLERY_STAGE_MS}ms ease`,
                      pointerEvents: twoDGalleryTransitionPhase === 'list-collapse-picker' ? 'none' : 'auto',
                    }}
                  >
                    {renderTwoDMediaPanel(false)}

                    {(focusedSwitchWhiteOverlayAnimation || twoDFocusedSwitchTitlePulse) ? (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          borderRadius: 14,
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.84), rgba(241,247,255,0.76))',
                          boxShadow: twoDFocusedSwitchTitlePulse
                            ? '0 0 28px rgba(255,255,255,0.86), inset 0 0 18px rgba(255,255,255,0.68)'
                            : 'none',
                          opacity: twoDFocusedSwitchTitlePulse ? 0.24 : 0,
                          backdropFilter: twoDFocusedSwitchTitlePulse ? 'blur(6px)' : 'blur(0px)',
                          WebkitBackdropFilter: twoDFocusedSwitchTitlePulse ? 'blur(6px)' : 'blur(0px)',
                          animation: focusedSwitchWhiteOverlayAnimation,
                          transition: `opacity ${TWO_D_GALLERY_STAGE_MS}ms ease, backdrop-filter ${TWO_D_GALLERY_STAGE_MS}ms ease, box-shadow ${TWO_D_GALLERY_STAGE_MS}ms ease`,
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ) : (
                <GalleryCategoryOverview
                  overviewClassName={collapseOverviewToViewer ? 'dse-gallery-category-overview--collapse-to-viewer' : ''}
                  categories={galleries}
                  previewByCategory={galleryCardPreviews}
                  getCategoryDescription={resolveGalleryCardDescription}
                  onCategoryActivate={(category) => {
                    const galleryId = String(category?.id || '').trim();
                    if (!galleryId || isTwoDDesktopTransitioning) return false;
                    logTerminalCommandExecution(`KLIKK::AAPNE GALLERI ${galleryId}`, '2D.GALLERY.FOCUS', `start focus transition for ${galleryId}`);
                    startTwoDFocusTransition(galleryId);
                    return false;
                  }}
                  getCategoryMotion={(gallery, idx) => {
                    const isTransitionTarget = String(gallery?.id || '') === String(twoDTransitionGalleryId || '');
                    const hideAllCards = twoDGalleryTransitionPhase === 'list-collapse-picker';
                    const fadeOthers = twoDGalleryTransitionPhase === 'focus-fade-others' && !isTransitionTarget;

                    let cardOpacity = reveal ? 1 : 0;
                    if (hideAllCards) {
                      cardOpacity = 0;
                    } else if (twoDGalleryTransitionPhase === 'focus-expand-picker') {
                      cardOpacity = isTransitionTarget ? 1 : 0;
                    } else if (twoDGalleryTransitionPhase === 'focus-collapse-overview') {
                      cardOpacity = isTransitionTarget ? 1 : 0;
                    } else if (
                      twoDGalleryTransitionPhase === 'focus-fade-others'
                      || twoDGalleryTransitionPhase === 'list-fade-in-others'
                    ) {
                      cardOpacity = 1;
                    }

                    return {
                      style: {
                        opacity: cardOpacity,
                        transform: cardOpacity > 0.99
                          ? `translateY(0px) scale(${isTransitionTarget && twoDGalleryTransitioningToFocus ? 1.02 : 1})`
                          : 'translateY(16px) scale(0.95)',
                        filter: fadeOthers
                          ? 'blur(5px) saturate(0.55) brightness(0.72)'
                          : (cardOpacity > 0.99 ? 'blur(0px)' : 'blur(3px)'),
                        transition: `opacity ${TWO_D_GALLERY_STAGE_MS}ms ease, transform ${TWO_D_GALLERY_STAGE_MS}ms ease, filter ${TWO_D_GALLERY_STAGE_MS}ms ease`,
                        transitionDelay: isTwoDDesktopTransitioning ? '0ms' : `${idx * 95}ms`,
                        pointerEvents: cardOpacity < 0.05 ? 'none' : 'auto',
                        zIndex: isTransitionTarget ? 2 : 1,
                      },
                    };
                  }}
                  isCategoryDisabled={() => isTwoDDesktopTransitioning}
                  emptyMessage="Ingen gallerier tilgjengelig akkurat naa."
                />
              )}

            </div>
          )}
        </div>

        {showTwoDTerminalControls ? (
          <AnimatedTerminalShell
            isOpen={showTwoDTerminal}
            onOpenChange={setShowTwoDTerminal}
            buttonLabel="TERMINAL"
            buttonTitleOpen="Skjul terminal"
            buttonTitleClosed="Vis terminal"
            buttonAriaOpen="Skjul terminal"
            buttonAriaClosed="Vis terminal"
            buttonStyle={{
              position: 'fixed',
              left: TWO_D_TERMINAL_BUTTON_LEFT,
              bottom: TWO_D_TERMINAL_BUTTON_BOTTOM,
              width: sharedTerminalButtonWidth,
              height: TWO_D_TERMINAL_BUTTON_HEIGHT,
              borderRadius: 10,
              fontSize: 14,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              opacity: threeDTransitionTerminalOpacity,
              filter: threeDTransitionTerminalFilter,
              transform: threeDTransitionTerminalTransform,
              transition: `opacity ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1), filter ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1), transform ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1)`,
            }}
            panelStyle={{
              position: 'fixed',
              left: TWO_D_TERMINAL_PANEL_LEFT,
              top: sharedTerminalPanelTop,
              opacity: threeDTransitionTerminalOpacity,
              filter: threeDTransitionTerminalFilter,
              transform: threeDTransitionTerminalTransform,
              transition: `opacity ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1), filter ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1), transform ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.24, 0.9, 0.24, 1)`,
            }}
            collapsedPanelStyle={{
              position: 'fixed',
              left: TWO_D_TERMINAL_BUTTON_LEFT,
              top: sharedTerminalButtonTop,
            }}
            baseWidth={TWO_D_TERMINAL_PANEL_WIDTH}
            baseHeight={TWO_D_TERMINAL_PANEL_HEIGHT}
            expandScale={TWO_D_TERMINAL_EXPAND_SCALE}
            expandSettleMs={TWO_D_TERMINAL_EXPAND_SETTLE_MS}
            panelZIndex={25}
            buttonZIndex={26}
          >
            {({ isExpanded, toggleExpand, requestClose }) => (
              <TerminalPanel
                compact={false}
                botOptions={bots}
                selectedBotId={selectedBot ? selectedBot.id : ''}
                logs={terminalLines}
                userLabel={userLabel}
                faqSelection={faqSelection}
                faqOptions={faqOptions}
                onFaqSelect={handleFaqSelect}
                showExploreButton
                onExploreClick={beginThreeDExplore}
                showBackTo2DButton={false}
                showNewsletterInput={showTerminalNewsletterInput}
                newsletterEmail={terminalNewsletterEmail}
                newsletterBusy={terminalNewsletterBusy}
                onNewsletterEmailChange={setTerminalNewsletterEmail}
                onNewsletterSubmit={submitNewsletterSignupFromTerminal}
                typewriterNow={terminalTypewriterNow}
                matrixOnlyMode={inThreeDTransition}
                headerScale={1.5}
                isExpanded={isExpanded}
                onToggleExpand={toggleExpand}
                onClose={() => requestClose(false)}
              />
            )}
          </AnimatedTerminalShell>
        ) : null}

        {showThreeDTransitionWhiteOverlay ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 8,
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(242,246,255,0.92))',
              opacity: isThreeDTransitionWhiteBlur ? 0.92 : 0.36,
              backdropFilter: isThreeDTransitionWhiteBlur ? 'blur(18px)' : 'blur(10px)',
              WebkitBackdropFilter: isThreeDTransitionWhiteBlur ? 'blur(18px)' : 'blur(10px)',
              transition: `opacity ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1), backdrop-filter ${THREE_D_TRANSITION_UI_MS}ms cubic-bezier(0.22, 0.84, 0.24, 1)`,
              overflow: 'hidden',
            }}
          />
        ) : null}

        {inThreeDOverview ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 9,
              pointerEvents: requireRotation || inThreeDTransition ? 'none' : 'auto',
              opacity: shouldHideThreeDSceneForTravel ? 0 : 1,
              transition: `opacity ${THREE_D_SCENE_OPACITY_TRANSITION_MS}ms ease, filter ${THREE_D_SCENE_FILTER_TRANSITION_MS}ms ease`,
              animation: threeDArrivalAnimation,
              transformOrigin: '50% 50%',
              willChange: inThreeDTransition ? 'filter, opacity' : 'auto',
              filter: overviewMode === 'to2d' ? 'blur(7px) brightness(0.66)' : 'none',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 45% 42%, rgba(31,58,95,0.35), rgba(4,5,8,0.96) 56%)' }} />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 7,
                background: 'radial-gradient(circle at 50% 90%, rgba(158,210,255,0.18), rgba(10,15,30,0) 60%)',
              }}
            />
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', perspective: '1600px', zIndex: 11 }}>
              <div style={{ position: 'relative', width: 'min(1200px, 94vw)', height: 'min(680px, 82vh)', transformStyle: 'preserve-3d' }}>
                {galleries.map((gallery, idx) => {
                  const isFocused = idx === focusIndex;
                  const isTitleHovered = isFocused && hoveredTitleIndex === idx;
                  const isOutgoing = !!focusSmokeTransition
                    && focusSmokeTransition.fromIndex !== focusSmokeTransition.toIndex
                    && idx === focusSmokeTransition.fromIndex;
                  const restingDirection = idx < focusIndex ? -1 : 1;
                  const outgoingDirection = focusSmokeTransition?.direction || restingDirection;

                  const x = isFocused
                    ? 0
                    : (isOutgoing ? (outgoingDirection > 0 ? -1540 : 1540) : restingDirection * 1880);
                  const y = isFocused ? 0 : 32;
                  const z = isFocused ? 0 : -300;
                  const rotateY = isFocused ? 0 : (restingDirection * 30);
                  const titleOpacity = isFocused ? 1 : (isOutgoing ? 0.22 : 0);
                  const extrusionDepthColor = isFocused ? 'rgba(24,45,86,0.99)' : 'rgba(12,22,43,0.96)';
                  const extrusionLayers = Array.from({ length: isFocused ? 16 : 11 }, (_, layerIndex) => `${layerIndex + 1}px ${layerIndex + 1}px 0 ${extrusionDepthColor}`).join(', ');
                  const titleFontSize = resolveOneLineTitleFontSize(gallery.title, isFocused);
                  const baseGlowLayer = isFocused
                    ? '0 0 42px rgba(250,204,21,0.62), 0 0 92px rgba(124,185,255,0.45)'
                    : '0 0 24px rgba(145,192,255,0.36)';
                  const hoverGlowLayer = isFocused
                    ? '0 0 64px rgba(250,204,21,0.86), 0 0 148px rgba(124,185,255,0.75)'
                    : baseGlowLayer;

                  return (
                    <button
                      key={`three-${gallery.id}`}
                      type="button"
                      onClick={() => {
                        if (idx === focusIndex) {
                          goToGallery(gallery.id);
                          return;
                        }
                        moveFocusTo(idx);
                      }}
                      onMouseEnter={() => {
                        if (isFocused) setHoveredTitleIndex(idx);
                      }}
                      onMouseLeave={() => {
                        setHoveredTitleIndex((prev) => (prev === idx ? null : prev));
                      }}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transformStyle: 'preserve-3d',
                        transform: `translate3d(${x}px, ${y}px, ${z}px) rotateY(${rotateY}deg) translate(-50%, -50%) scale(${isFocused ? 1.2 : 0.84})`,
                        border: 'none',
                        background: 'transparent',
                        color: '#f6f8ff',
                        padding: 0,
                        width: 'min(900px, 96vw)',
                        minHeight: 236,
                        cursor: isFocused ? 'pointer' : 'default',
                        textAlign: 'center',
                        transition: `transform 760ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${THREE_D_ITEM_OPACITY_TRANSITION_MS}ms ease`,
                        fontFamily: 'Times New Roman, Georgia, serif',
                        letterSpacing: 0.7,
                        opacity: titleOpacity,
                        pointerEvents: isFocused ? 'auto' : 'none',
                      }}
                    >
                      <div
                        style={{
                          fontSize: titleFontSize,
                          fontWeight: 900,
                          lineHeight: 0.88,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: isFocused ? '#fff2cc' : '#dfebff',
                          textShadow: `${extrusionLayers}, ${isTitleHovered ? hoverGlowLayer : baseGlowLayer}`,
                          maxWidth: '100%',
                          whiteSpace: 'nowrap',
                          overflowWrap: 'normal',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          padding: '12px 16px',
                          transform: `translateZ(${isFocused ? 44 : 20}px) scale(${isTitleHovered ? 1.05 : 1})`,
                          transition: 'transform 2s ease, text-shadow 3s ease 2s',
                        }}
                      >
                        {gallery.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {galleries.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const nextIndex = focusIndex === 0 ? galleries.length - 1 : focusIndex - 1;
                    moveFocusTo(nextIndex, -1);
                  }}
                  style={{
                    position: 'absolute',
                    left: 22,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 68,
                    height: 68,
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.5)',
                    background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), rgba(0,0,0,0.8) 65%)',
                    color: '#fff',
                    fontSize: 36,
                    cursor: 'pointer',
                    boxShadow: '0 16px 32px rgba(0,0,0,0.48)',
                    zIndex: 12,
                  }}
                  aria-label="Previous gallery"
                  title="Previous gallery"
                >
                  ❮
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const nextIndex = (focusIndex + 1) % galleries.length;
                    moveFocusTo(nextIndex, 1);
                  }}
                  style={{
                    position: 'absolute',
                    right: 22,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 68,
                    height: 68,
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.5)',
                    background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), rgba(0,0,0,0.8) 65%)',
                    color: '#fff',
                    fontSize: 36,
                    cursor: 'pointer',
                    boxShadow: '0 16px 32px rgba(0,0,0,0.48)',
                    zIndex: 12,
                  }}
                  aria-label="Next gallery"
                  title="Next gallery"
                >
                  ❯
                </button>
              </>
            ) : null}

            <AnimatedTerminalShell
              isOpen={showThreeDTerminal}
              onOpenChange={setShowThreeDTerminal}
              buttonLabel="TERMINAL"
              buttonTitleOpen="Skjul terminal"
              buttonTitleClosed="Vis terminal"
              buttonAriaOpen="Skjul terminal"
              buttonAriaClosed="Vis terminal"
              buttonStyle={{
                position: 'fixed',
                left: TWO_D_TERMINAL_BUTTON_LEFT,
                bottom: TWO_D_TERMINAL_BUTTON_BOTTOM,
                width: sharedTerminalButtonWidth,
                height: TWO_D_TERMINAL_BUTTON_HEIGHT,
                borderRadius: 10,
                fontSize: 14,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
              }}
              panelStyle={{
                position: 'fixed',
                left: TWO_D_TERMINAL_PANEL_LEFT,
                top: sharedTerminalPanelTop,
              }}
              collapsedPanelStyle={{
                position: 'fixed',
                left: TWO_D_TERMINAL_BUTTON_LEFT,
                top: sharedTerminalButtonTop,
              }}
              baseWidth={TWO_D_TERMINAL_PANEL_WIDTH}
              baseHeight={TWO_D_TERMINAL_PANEL_HEIGHT}
              expandScale={TWO_D_TERMINAL_EXPAND_SCALE}
              expandSettleMs={TWO_D_TERMINAL_EXPAND_SETTLE_MS}
              buttonZIndex={26}
              panelZIndex={25}
            >
              {({ isExpanded, toggleExpand, requestClose }) => (
                <TerminalPanel
                  compact={false}
                  botOptions={bots}
                  selectedBotId={selectedBot ? selectedBot.id : ''}
                  logs={terminalLines}
                  userLabel={userLabel}
                  faqSelection={faqSelection}
                  faqOptions={faqOptions}
                  onFaqSelect={handleFaqSelect}
                  showExploreButton={false}
                  showBackTo2DButton
                  onBackTo2DClick={shutdownTo2D}
                  showNewsletterInput={showTerminalNewsletterInput}
                  newsletterEmail={terminalNewsletterEmail}
                  newsletterBusy={terminalNewsletterBusy}
                  onNewsletterEmailChange={setTerminalNewsletterEmail}
                  onNewsletterSubmit={submitNewsletterSignupFromTerminal}
                  typewriterNow={terminalTypewriterNow}
                  isExpanded={isExpanded}
                  onToggleExpand={toggleExpand}
                  onClose={() => requestClose(false)}
                />
              )}
            </AnimatedTerminalShell>
          </div>
        ) : null}
      </div>

    </div>
  );
};

export default GalleryPicker3D;
